/**
 * Per-section extracted_data field schemas (mirrors document-service compliance_rules.py).
 * Used by SectionExtractedDataEditor for structured editing.
 */

const SCORE_SUBFIELDS = [
  { key: 'ss', type: 'string', label: 'Standard Score' },
  { key: 'description', type: 'text', label: 'Description' },
];

const COGNITIVE_SCORE_SUBFIELDS = [
  { key: 'ss', type: 'string', label: 'Standard Score' },
  { key: 'percentile', type: 'string', label: 'Percentile' },
  { key: 'description', type: 'text', label: 'Description' },
];

const PERCENTILE_SUBFIELDS = [
  { key: 'ss', type: 'string', label: 'T-Score' },
  { key: 'percentile', type: 'string', label: 'Percentile' },
  { key: 'description', type: 'text', label: 'Description' },
];

const ADAPTIVE_SCORE_SUBFIELDS = [
  { key: 'ss', type: 'string', label: 'Standard Score' },
  { key: 'percentile', type: 'string', label: 'Percentile Rank' },
  { key: 'description', type: 'text', label: 'Description' },
  { key: 'strength_weakness', type: 'string', label: 'Strength/Weakness' },
  { key: 'adaptive_level', type: 'string', label: 'Adaptive Level' },
];

const ADAPTIVE_ASSESSMENT_ITEM_FIELDS = [
  { key: 'test_name', type: 'string', label: 'Test Name' },
  { key: 'responder', type: 'string', label: 'Responder' },
  { key: 'responder_type', type: 'string', label: 'Responder Type (parent / teacher)' },
  { key: 'examiner', type: 'string', label: 'Examiner' },
  { key: 'date', type: 'date', label: 'Date' },
  { key: 'adaptive_level', type: 'string', label: 'Adaptive Level' },
  { key: 'summary', type: 'text', label: 'Summary' },
  { key: 'communication', type: 'object', label: 'Communication', fields: ADAPTIVE_SCORE_SUBFIELDS },
  { key: 'daily_living_skills', type: 'object', label: 'Daily Living Skills', fields: ADAPTIVE_SCORE_SUBFIELDS },
  { key: 'socialization', type: 'object', label: 'Socialization', fields: ADAPTIVE_SCORE_SUBFIELDS },
  { key: 'adaptive_behavior_composite', type: 'object', label: 'Adaptive Behavior Composite', fields: ADAPTIVE_SCORE_SUBFIELDS },
  { key: 'motor_skills', type: 'object', label: 'Motor Skills', fields: ADAPTIVE_SCORE_SUBFIELDS },
  { key: 'full_results', type: 'text', label: 'Full Results' },
];

/** @deprecated single-row — use adaptive_assessments list */
const ADAPTIVE_ASSESSMENT_FIELDS = ADAPTIVE_ASSESSMENT_ITEM_FIELDS;

const BEHAVIORAL_ASSESSMENT_ITEM_FIELDS = [
  { key: 'test_name', type: 'string', label: 'Test Name' },
  { key: 'tool', type: 'string', label: 'Tool' },
  { key: 'form', type: 'string', label: 'Form' },
  { key: 'responder', type: 'string', label: 'Responder' },
  { key: 'responder_type', type: 'string', label: 'Responder Type (parent / teacher)' },
  { key: 'examiner', type: 'string', label: 'Examiner' },
  { key: 'date', type: 'date', label: 'Date' },
  { key: 'summary', type: 'text', label: 'Summary' },
  { key: 'full_results', type: 'text', label: 'Full Results' },
  { key: 'hyperactivity', type: 'object', label: 'Hyperactivity', fields: SCORE_SUBFIELDS },
  { key: 'aggression', type: 'object', label: 'Aggression', fields: SCORE_SUBFIELDS },
  { key: 'conduct_problems', type: 'object', label: 'Conduct Problems', fields: SCORE_SUBFIELDS },
  { key: 'anxiety', type: 'object', label: 'Anxiety', fields: SCORE_SUBFIELDS },
  { key: 'depression', type: 'object', label: 'Depression', fields: SCORE_SUBFIELDS },
  { key: 'somatization', type: 'object', label: 'Somatization', fields: SCORE_SUBFIELDS },
  { key: 'atypicality', type: 'object', label: 'Atypicality', fields: SCORE_SUBFIELDS },
  { key: 'withdrawal', type: 'object', label: 'Withdrawal', fields: SCORE_SUBFIELDS },
  { key: 'attention_problems', type: 'object', label: 'Attention Problems', fields: SCORE_SUBFIELDS },
  { key: 'learning_problems', type: 'object', label: 'Learning Problems', fields: SCORE_SUBFIELDS },
  { key: 'adaptability', type: 'object', label: 'Adaptability', fields: SCORE_SUBFIELDS },
  { key: 'social_skills', type: 'object', label: 'Social Skills', fields: SCORE_SUBFIELDS },
  { key: 'leadership', type: 'object', label: 'Leadership', fields: SCORE_SUBFIELDS },
  { key: 'activities_of_daily_living', type: 'object', label: 'Activities of Daily Living', fields: SCORE_SUBFIELDS },
  { key: 'functional_communication', type: 'object', label: 'Functional Communication', fields: SCORE_SUBFIELDS },
  { key: 'study_skills', type: 'object', label: 'Study Skills', fields: SCORE_SUBFIELDS },
  { key: 'externalizing_problems', type: 'object', label: 'Externalizing Problems', fields: SCORE_SUBFIELDS },
  { key: 'internalizing_problems', type: 'object', label: 'Internalizing Problems', fields: SCORE_SUBFIELDS },
  { key: 'school_problems', type: 'object', label: 'School Problems', fields: SCORE_SUBFIELDS },
  { key: 'behavioral_symptoms_index', type: 'object', label: 'Behavioral Symptoms Index', fields: PERCENTILE_SUBFIELDS },
  { key: 'adaptive_skills_index', type: 'object', label: 'Adaptive Skills Index', fields: PERCENTILE_SUBFIELDS },
];

const BEHAVIORAL_SCALE_KEYS = [
  'hyperactivity', 'aggression', 'conduct_problems',
  'anxiety', 'depression', 'somatization',
  'atypicality', 'withdrawal', 'attention_problems', 'learning_problems',
  'adaptability', 'social_skills', 'leadership',
  'activities_of_daily_living', 'functional_communication', 'study_skills',
  'externalizing_problems', 'internalizing_problems', 'school_problems',
  'behavioral_symptoms_index', 'adaptive_skills_index',
];

const BASC_T_MIN = 20;
const BASC_T_MAX = 120;

const FORM_EMBED_NARRATIVE_KEYS = [
  'consent_for_evaluation', 'placement_review', 'dibels_report',
  'notice_of_meeting', 'iep_compliance', 'vision_hearing_screenings',
];

const FORM_EMBED_NARRATIVE_RE = new RegExp(
  `["']?(?:${FORM_EMBED_NARRATIVE_KEYS.join('|')})["']?\\s*:`,
  'i',
);

/** True when text looks like a form/JSON block rather than assessment narrative. */
export function looksLikeFormJsonBlob(text) {
  if (text == null || text === '') return false;
  const raw = String(text).trim();
  if (!raw) return false;
  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return FORM_EMBED_NARRATIVE_KEYS.some((k) => k in parsed);
      }
    } catch {
      /* partial JSON */
    }
  }
  return FORM_EMBED_NARRATIVE_RE.test(raw);
}

/** Placeholder/instruction text the LLM sometimes copies verbatim from the prompt schema
 *  (e.g. "FULL results text ...", "COMPLETE cognitive results narrative verbatim"). This is
 *  NOT real document data, so we hide it rather than show wrong/template text. */
const PROMPT_ECHO_RE = new RegExp(
  [
    'complete\\s+(?:\\w+\\s+)?results?\\s+narrative',
    'full\\s+results?\\s+text',
    'results?\\s+text\\s+(?:exactly\\s+as\\s+written|with\\s+all)',
    'exactly\\s+as\\s+written\\s+in\\s+(?:the\\s+)?document',
    'results?\\s+narrative\\s+(?:with\\s+all|verbatim)',
    'narrative\\s+verbatim',
    'all\\s+scores?(?:\\s*,)?\\s+and\\s+descriptions?',
    'clinical\\s+scales?\\s+and\\s+adaptive\\s+scales?',
    'brief\\s+summary\\s+of\\s+(?:adaptive|behavioral)\\s+findings',
    'legacy\\s+single\\s+adaptive\\s+row',
  ].join('|'),
  'i'
);

export function looksLikePromptEcho(text) {
  if (text == null || text === '') return false;
  return PROMPT_ECHO_RE.test(String(text));
}

/** Strip embedded form JSON from assessment summary/full_results text. */
export function sanitizeAssessmentNarrative(text) {
  if (text == null || text === '') return '';
  const raw = String(text).trim();
  if (!raw) return '';

  // Drop prompt/template placeholder text so it never renders as real data.
  if (PROMPT_ECHO_RE.test(raw)) return '';

  if (looksLikeFormJsonBlob(raw)) {
    if (raw.startsWith('{')) {
      try {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
          for (const key of ['summary', 'full_results', 'results', 'narrative', 'description']) {
            const inner = obj[key];
            if (typeof inner === 'string' && inner.trim() && !looksLikeFormJsonBlob(inner)) {
              return inner.trim().slice(0, 4000);
            }
          }
          return '';
        }
      } catch {
        /* fall through */
      }
    }
    const match = raw.match(/^(Clinical Scales:[\s\S]*?)(?=["']?(?:consent_for_evaluation|placement_review|dibels_report)\b)/i);
    if (match && match[1].trim().length > 40) {
      return match[1].trim().slice(0, 4000);
    }
    const idx = raw.search(FORM_EMBED_NARRATIVE_RE);
    if (idx > 40) return raw.slice(0, idx).trim().slice(0, 4000);
    return '';
  }

  return raw.slice(0, 4000);
}

function behavioralRowHasStructuredScores(row) {
  if (!row || typeof row !== 'object') return false;
  return BEHAVIORAL_SCALE_KEYS.some((key) => {
    const sub = row[key];
    return sub && typeof sub === 'object' && (
      sub.ss != null || sub.SS != null || sub.percentile != null || sub.t_score != null
    );
  });
}

function normalizeBehavioralSubscale(sub) {
  if (!sub || typeof sub !== 'object' || Array.isArray(sub)) return sub;
  return {
    ss: sub.ss ?? sub.SS ?? sub.t_score ?? sub.score ?? '',
    percentile: sub.percentile ?? sub.percentile_rank ?? '',
    description: sub.description ?? sub.desc ?? sub.range ?? '',
  };
}

const COGNITIVE_INDEX_KEYS = [
  'verbal_comprehension',
  'visual_spatial',
  'fluid_reasoning',
  'working_memory',
  'processing_speed',
];

export const HIDDEN_EXTRACTED_KEYS = new Set(['gender', 'percentage_general_ed', 'rights_notice_provided', '_evidence_spans', 'printed_reevaluation_due_date', 'field_source_pages']);

/** Sections that should not show the leftover extra-keys panel under the schema fields. */
export const HIDE_EXTRA_DATA_PANEL = new Set(['identity_timeline']);

/** Internal/plumbing keys are underscore-prefixed (e.g. _form_anchor_pages,
 * _evidence_spans) and must never render in the UI. */
export function isHiddenExtractedKey(key) {
  return typeof key === 'string' && (key.startsWith('_') || HIDDEN_EXTRACTED_KEYS.has(key));
}

/** Legacy accommodations keys the backend still stores for compatibility but that the
 * restructured section (Instructional / State-Wide / LEA) no longer displays. Hidden
 * per-section only (assessment_accommodations is a real field in assessment_participation,
 * so it must not be hidden globally). Keeps them out of the "Additional Data" panel. */
export const RETIRED_EXTRA_KEYS = {
  accommodations: new Set([
    'assessment',
    'assessment_accommodations',
    'assistive_technology',
    'behavior_supports',
    'instructional_accommodations',
    'instructional_accommodations_detailed',
    // Mirrored from the assessment addendum so the merged Services view can pair codes
    // with their accommodations; Assessment Participation is where they are reviewed.
    'participation_by_assessment',
    'transportation_provided',
    // Table 1 is the canonical grid; these lists are the same names again.
    'statewide_assessment_accommodations',
    'lea_assessment_accommodations',
    'statewide_accommodations',
    'lea_accommodations',
    'student_name',
    'accommodations_table',
    'additional_data',
    'essential_elements_instruction',
    'essential_elements_checked',
    'essential_elements',
  ]),
  services_lre: new Set([
    'student_name',
    'daily_minutes',
    'percentage_general_ed',
    'additional_data',
    'essential_elements_instruction',
    'essential_elements_checked',
    'essential_elements',
  ]),
  // Repetitive on the participation grid: the tools and data sources are reviewed in
  // Evaluation, and the subject areas are already the assessment rows themselves.
  assessment_participation: new Set([
    'subject_areas_assessed',
    'areas_of_assessment',
    'data_sources_reviewed',
    'assessment_tools_used',
    // Table 1 lives once in Accommodations — do not dump it again here.
    'assessment',
    'student_name',
    'lea_accommodations',
    'statewide_accommodations',
    'accommodations_table1',
    'accommodations_table',
    'essential_elements_checked',
    'alternate_assessment_consideration',
    'alternate_assessment_justification',
    'additional_data',
  ]),
  eligibility_compliance: new Set([
    'consent_obtained',
    'vision_hearing_screenings',
    'additional_data',
  ]),
  eligibility: new Set([
    'eligibility_meeting_date',
    'meeting_date',
    'student_name',
    'additional_data',
  ]),
  // Absorbed by a displayed field: IEP Start Date is Initial Placement, IEP End Date and
  // Annual Review Date are the single Annual IEP Due Date, School Name is one entry of
  // Previous Schools. The rest is grade-projection plumbing, not a document value.
  identity_timeline: new Set([
    'iep_start_date',
    'iep_end_date',
    'iep_due_date',
    'annual_review_date',
    'expected_iep_end_date',
    'school_name',
    'age_document_text',
    'computed_grade',
    'grade_computation_note',
    'field_overrides',
  ]),
  present_levels: new Set([
    'grade_level',
    'academic_performance',
    'baseline_data',
    'additional_data',
  ]),
  goals: new Set([
    'goals',
    'total_goals',
    'additional_data',
    'essential_elements_instruction',
  ]),
  behavior: new Set([
    'source',
    'pages',
    'additional_data',
  ]),
  transition_plan: new Set([
    'post_secondary_education',
    'post_secondary_employment',
    'independent_living',
    'transition_assessments',
    'transition_services',
    'responsible_agencies',
    'multi_year_course_of_study',
    'additional_data',
  ]),
  iep_compliance: new Set([
    'iep_compliance',
    'post_secondary_education',
    'post_secondary_employment',
    'independent_living',
    'transition_assessments',
    'transition_services',
    'responsible_agencies',
    'transition_exempt',
    'exempt_reason',
    'student_age',
    'rights_notice_provided',
    'additional_data',
  ]),
};

export const HIDE_SECTION_FINDINGS = new Set([
  'present_levels',
  'goals',
  'services_lre',
  'accommodations',
  'assessment_participation',
  'eligibility_compliance',
  'behavior',
  'transition_plan',
  'iep_compliance',
]);

export const COMPLIANCE_CHECKLIST_STATUSES = [
  'Addressed',
  'Not Addressed',
  'Needs Review',
  'N/A',
];

export const IEP_COMPLIANCE_CHECKLIST_STATUSES = [
  'Compliant',
  'Not Compliant',
  'Needs Review',
  'N/A',
];

const SPECIAL_FACTOR_ENUMS = {
  language_needs: ['no_lep_needs', 'lep_addressed_in_iep'],
  braille_needs: ['no_braille_needed', 'braille_addressed_in_iep'],
  communication_needs: ['no_communication_needs', 'communication_addressed_in_iep'],
  assistive_technology: ['no_at_needs', 'at_addressed_in_iep'],
  behavior_strategy: ['no_behavior_needs', 'behavior_addressed_in_iep'],
};

export function isRetiredExtraKey(sectionKey, key) {
  return Boolean(RETIRED_EXTRA_KEYS[sectionKey]?.has(key));
}

/** Heading for the read-only panel of extras below a section's schema fields. Identity &
 * Timeline only ever carries date material there, and reviewers asked for it by name. */
const EXTRA_DATA_HEADINGS = {};

export function extraDataHeading(sectionKey) {
  return EXTRA_DATA_HEADINGS[sectionKey] || 'Additional Data';
}

/** Schema field keys hidden from display but still stored/saved. */
export const HIDDEN_SCHEMA_FIELD_KEYS = {
  identity_timeline: new Set(['iep_start_date']),
  iep_compliance: new Set(['rights_notice_provided']),
  services_lre: new Set(['provider']),
  evaluation: new Set([
    'adaptive_assessment',
    'behavioral_assessment',
    'domains_assessed',
    'pwn_assessment_areas',
    'data_review_assessment_areas',
    'student_name',
    'disability_category',
  ]),
  present_levels: new Set([
    'plaafp_areas',
    'reading_level',
    'writing_level',
    'math_level',
    'academic_performance',
    'functional_performance',
    'strengths',
    'weaknesses',
    'impact_on_curriculum',
    'baseline_data',
    'grade_level',
  ]),
};

/** Finding descriptions hidden from compliance UI per tracker request. */
export const HIDDEN_FINDING_DESCRIPTIONS = new Set([
  'Service providers identified',
  'Notice of rights provided',
  'Disability category documented',
  'Team determination recorded',
  'Exclusionary factors addressed',
  'Team signatures and meeting dates aligned',
  'Translator/interpreter documented',
  'Notice of Meeting requirements met',
]);

export function isSchemaFieldHidden(sectionKey, fieldKey) {
  if (HIDDEN_EXTRACTED_KEYS.has(fieldKey)) return true;
  const hidden = HIDDEN_SCHEMA_FIELD_KEYS[sectionKey];
  return Boolean(hidden?.has(fieldKey));
}

export function filterHiddenFindings(findings) {
  return (findings || []).filter(
    (f) => !HIDDEN_FINDING_DESCRIPTIONS.has(String(f?.description || '').trim()),
  );
}

/** Findings list for UI — positives only (negatives live under human_review_flags). */
export function filterPositiveFindings(findings) {
  return filterHiddenFindings(findings).filter(
    (f) => f && f.passed !== false,
  );
}

/** Count failed findings visible in the UI (excludes hidden + dismissed). Legacy fallback. */
export function countActionableFailedFindings(section, dismissedFindings = []) {
  const dismissed = new Set(dismissedFindings || []);
  return filterHiddenFindings(section?.findings || []).filter(
    (f) => f && f.passed === false && !dismissed.has(f.description) && !f.dismissed,
  ).length;
}

/** Actionable human-review flags (excludes info-only and staff-dismissed). */
export function countActionableHumanReviewFlags(section) {
  const raw = section?.human_review_flags;
  if (!Array.isArray(raw)) return 0;
  const norm = (s) => String(s || '').toLowerCase().split(/\s+/).filter(Boolean).join(' ');
  // Folded flags carry the original finding description as their message, so a
  // dismissed finding matches by message/code and must not keep counting.
  const dismissed = new Set((section?.dismissed_findings || []).map(norm));
  return raw.filter((f) => {
    if (!f) return false;
    if (typeof f === 'string') return Boolean(f.trim()) && !dismissed.has(norm(f));
    if (String(f.severity || 'warning').toLowerCase() === 'info') return false;
    const keys = [f.message, f.reason, f.description, f.code].filter(Boolean).map(norm);
    return keys.length > 0 && !keys.some((k) => dismissed.has(k));
  }).length;
}

export function mapComplianceStatus(status) {
  if (status === 'compliant') return 'compliant';
  if (status === 'needs_attention') return 'needs_attention';
  if (status === 'not_applicable') return 'not_applicable';
  return 'non_compliant';
}

/** Derive section badge status from human review (legacy: failed findings). */
export function deriveSectionDisplayStatus(sectionKey, section) {
  if (!section || typeof section !== 'object') return 'compliant';
  if (section.status === 'not_applicable') return 'not_applicable';

  const issueCount = deriveSectionIssueCount(sectionKey, section);
  if (issueCount >= 2) return 'non_compliant';
  if (issueCount === 1) return 'needs_attention';
  return mapComplianceStatus(section.status) || 'compliant';
}

export function deriveSectionIssueCount(sectionKey, section) {
  if (!section) return 0;
  if (section.status === 'not_applicable') return 0;
  const fromHr = countActionableHumanReviewFlags(section);
  if (fromHr > 0) return fromHr;
  const fromFindings = countActionableFailedFindings(section, section.dismissed_findings);
  if (fromFindings > 0) return fromFindings;
  return section.issues_count || 0;
}

function asDataList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function isFilledData(value) {
  if (value == null || value === '') return false;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(isFilledData);
  if (typeof value === 'object') {
    return Object.entries(value).some(([k, v]) => !String(k).startsWith('_') && isFilledData(v));
  }
  return String(value).trim() !== '';
}

function countGoalRows(ed) {
  const tables = asDataList(ed?.goals_tables);
  let n = 0;
  tables.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const nested = asDataList(row.goals || row.annual_goals);
    if (nested.length) {
      n += nested.filter((g) => g && (g.goal_area || g.annual_goal || g.goal || g.description)).length;
      return;
    }
    if (row.goal_area || row.annual_goal || row.goal || row.description) n += 1;
  });
  if (n) return n;
  return asDataList(ed?.goals).filter((g) => g && (g.goal_area || g.annual_goal || g.goal || g.description)).length;
}

const CHECKLIST_OK = new Set(['compliant', 'addressed']);

function countChecklistProgress(rows) {
  let ok = 0;
  let open = 0;
  asDataList(rows).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const status = String(row.status || '').trim();
    if (!status || /^n\/?a$/i.test(status)) return;
    if (CHECKLIST_OK.has(status.toLowerCase())) ok += 1;
    else open += 1;
  });
  return { ok, open, total: ok + open };
}

/** Meaningful extracted items for a section — used so progress is never 0 when data exists. */
export function countSectionDataPoints(sectionKey, section) {
  const ed = safeNormalizeSectionExtractedData(sectionKey, section);
  switch (sectionKey) {
    case 'identity_timeline':
      return [
        'student_name', 'date_of_birth', 'grade', 'iep_annual_review_date',
        'reevaluation_due_date', 'eligibility_classification', 'case_manager',
        'parent_name', 'school_name', 'student_id',
      ].filter((k) => isFilledData(ed[k])).length;
    case 'evaluation': {
      let n = 0;
      n += asDataList(ed.assessment_data).filter((r) => r && typeof r === 'object').length;
      n += asDataList(ed.adaptive_assessments).length;
      n += asDataList(ed.behavioral_assessments).length;
      n += asDataList(ed.academic_standardized).length;
      n += asDataList(ed.classroom_assessments).length;
      n += asDataList(ed.dibels_assessments).length;
      n += asDataList(ed.standardized_assessments).length;
      if (ed.cognitive_assessment && (ed.cognitive_assessment.test_name || ed.cognitive_assessment.fsiq || ed.cognitive_assessment.tool)) n += 1;
      if (isFilledData(ed.disability_category) || isFilledData(ed.domains_assessed)) n += 1;
      return n;
    }
    case 'present_levels':
      return asDataList(ed.present_levels_table || ed.plaafp_areas).filter((r) => r && r.area).length;
    case 'goals':
      return countGoalRows(ed);
    case 'services_lre':
      return asDataList(ed.services).filter((r) => r && (r.service_type || r.frequency || r.duration)).length
        + (isFilledData(ed.lre_justification) ? 1 : 0)
        + (isFilledData(ed.placement_type) ? 1 : 0);
    case 'accommodations':
      return asDataList(ed.accommodations_table1).filter((r) => r && r.description).length
        || asDataList(ed.instructional).length;
    case 'assessment_participation':
      return asDataList(ed.participation_by_assessment).length
        || asDataList(ed.lea_school_assessments).length;
    case 'behavior':
      return asDataList(ed.behavior_documents).filter((d) => d && (d.type || d.label)).length;
    case 'transition_plan':
      if (ed.transition_exempt || ed.no_transition_plan_needed) return 1;
      return countChecklistProgress(ed.transition_checklist).ok;
    case 'iep_compliance': {
      const checklistOk = countChecklistProgress(ed.iep_compliance_checklist).ok;
      let n = 0;
      const nom = ed.notice_of_meeting;
      if (nom && typeof nom === 'object' && (
        isFilledData(nom.date) || isFilledData(nom.meeting_date)
        || isFilledData(nom.purpose) || asDataList(nom.checked_boxes).length
      )) n += 1;
      if (asDataList(ed.team_members).some((m) => m && (isFilledData(m.name) || isFilledData(m.role)))) n += 1;
      if (isFilledData(ed.special_factors)) n += 1;
      if (ed.esy_considered != null || isFilledData(ed.esy_decision_status)
        || ed.regression_recoupment_documented != null) n += 1;
      if (ed.parent_participated != null) n += 1;
      if (isFilledData(ed.iep_meeting_date)) n += 1;
      if (isFilledData(ed.placement_review)) n += 1;
      return Math.max(n, checklistOk);
    }
    case 'eligibility_compliance':
      return [
        'eligibility_category', 'eligibility_determination',
        'eligibility_meeting_date', 'parent_guardian_participation',
      ].filter((k) => isFilledData(ed[k])).length;
    default:
      return Object.entries(ed || {}).filter(([k, v]) => !String(k).startsWith('_') && isFilledData(v)).length;
  }
}

/** Section completion 0–100 from extracted data + findings vs open review items. */
export function deriveSectionCompletionPct(sectionKey, section) {
  if (!section || typeof section !== 'object') return 0;
  if (section.status === 'not_applicable') return 100;

  const checklistKey = sectionKey === 'iep_compliance'
    ? 'iep_compliance_checklist'
    : sectionKey === 'transition_plan'
      ? 'transition_checklist'
      : null;
  if (checklistKey) {
    const ed = safeNormalizeSectionExtractedData(sectionKey, section);
    const checklist = countChecklistProgress(ed?.[checklistKey]);
    // Only use the checklist ratio when something is actually marked complete.
    // A defaulted all-Not-Compliant IEP list must not pin the ring at 0% when
    // NOM / team / ESY / special factors were extracted.
    if (checklist.total > 0 && checklist.ok > 0) {
      // Open review items on the IEP Compliance card are part of what is left to do.
      const open = sectionKey === 'iep_compliance' ? countActionableHumanReviewFlags(section) : 0;
      return Math.round((checklist.ok / (checklist.total + open)) * 100);
    }
    if (ed?.transition_exempt || ed?.no_transition_plan_needed) return 100;
  }

  const passed = HIDE_SECTION_FINDINGS.has(sectionKey)
    ? 0
    : filterPositiveFindings(section.findings || []).length;
  const dataPoints = countSectionDataPoints(sectionKey, section);
  const positives = Math.max(passed, dataPoints);
  const issues = countActionableHumanReviewFlags(section);
  const total = positives + issues;
  if (total > 0) {
    return Math.round((positives / total) * 100);
  }

  const status = section.status || '';
  if (status === 'compliant') return 100;
  if (status === 'needs_attention') return 50;
  if (dataPoints > 0) return status === 'non_compliant' ? 40 : 60;
  return 0;
}

/** Overall progress = average of applicable section completion percentages. */
export function deriveOverallComplianceProgress(compliance) {
  if (!compliance || typeof compliance !== 'object') return 0;
  const keys = [
    'identity_timeline', 'evaluation', 'present_levels', 'goals',
    'services_lre', 'accommodations', 'assessment_participation', 'behavior',
    'transition_plan', 'iep_compliance', 'eligibility_compliance',
  ];
  const pcts = [];
  keys.forEach((key) => {
    const sec = compliance[key];
    if (!sec || sec.status === 'not_applicable') return;
    const pct = deriveSectionCompletionPct(key, sec);
    if (pct == null) return;
    pcts.push(Math.max(0, Math.min(100, pct)));
  });
  if (!pcts.length) return 0;
  return Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length);
}

export const COMPLIANCE_SECTION_SCHEMAS = {
  identity_timeline: {
    label: 'Identity & Timeline',
    fields: [
      // Client email field set + dates. LEA / GenEd belong on the IEP team page only —
      // never on Identity.
      { key: 'student_name', type: 'string', label: 'Student Name' },
      { key: 'student_id', type: 'string', label: 'Student ID' },
      { key: 'date_of_birth', type: 'date', label: 'DOB' },
      { key: 'age', type: 'string', label: 'Age' },
      { key: 'meeting_date', type: 'date', label: 'IEP Meeting Date', fallbackKeys: ['iep_start_date', 'iep_meeting_date'] },
      { key: 'age_at_iep_meeting', type: 'string', label: 'Age at IEP Meeting' },
      { key: 'grade', type: 'string', label: 'Grade' },
      { key: 'school_name', type: 'string', label: 'School', fallbackKeys: ['previous_schools'] },
      { key: 'eligibility_classification', type: 'string', label: 'Eligibility Classification' },
      { key: 'initial_placement_date', type: 'date', label: 'Initial Placement Date' },
      { key: 'reevaluation_due_date', type: 'date', label: 'Reevaluation Due Date', fallbackKeys: ['eligibility_due_date'] },
      { key: 'iep_annual_review_date', type: 'date', label: 'IEP Due Date', fallbackKeys: ['iep_due_date', 'annual_review_date', 'iep_end_date'] },
      { key: 'case_manager', type: 'string', label: 'SPED Teacher', fallbackKeys: ['special_education_teacher'] },
      { key: 'parent_name', type: 'string', label: 'Parent/Guardian Name' },
      { key: 'parent_phone', type: 'string', label: 'Parent Phone' },
      { key: 'parent_address', type: 'text', label: 'Parent Address' },
      { key: 'district_name', type: 'string', label: 'District' },
      { key: 'previous_schools', type: 'string_list', label: 'Previous Schools' },
      { key: 'grade_at_initial_placement', type: 'string', label: 'Grade at Initial Placement' },
    ],
  },

  evaluation: {
    label: 'Evaluation',
    fields: [
      // Client-requested precise header block (most current evaluation cycle)
      { key: 'eligibility_classification', type: 'string', label: 'Most Current Eligibility Classification', fallbackKeys: ['disability_category'] },
      {
        key: 'data_review',
        type: 'object',
        label: 'Most Current Data Review',
        fields: [
          { key: 'occurred', type: 'boolean', label: 'Occurred' },
          { key: 'date', type: 'date', label: 'Date' },
        ],
      },
      { key: 'data_sufficient', type: 'boolean', label: 'Data Review — Was Data Sufficient?' },
      { key: 'consent_signature_date', type: 'date', label: 'PWN + Consent to Evaluate Signature Date' },
      { key: 'areas_of_assessment', type: 'string_list', label: 'Assessments Checked on Current Consent' },
      { key: 'pwn_assessment_areas', type: 'string_list', label: 'PWN Assessment Areas' },
      { key: 'data_review_assessment_areas', type: 'string_list', label: 'Data Review Assessment Areas' },
      { key: 'pwn_matches_data_review', type: 'boolean', label: 'Does Data Review Match PWN?' },
      { key: 'dual_eligibility', type: 'string', label: 'Dual Eligibility' },
      { key: 'assessment_data', type: 'assessment_data_table', label: 'Assessment Data' },
      { key: 'domains_assessed', type: 'string_list', label: 'Assessment Data' },
      {
        key: 'classroom_assessments',
        type: 'object_list',
        label: 'Classroom Assessments',
        itemLabel: 'Assessment',
        itemFields: [
          { key: 'tool', type: 'string', label: 'Tool' },
          { key: 'date', type: 'date', label: 'Date' },
          { key: 'results', type: 'text', label: 'Results' },
          { key: 'strengths', type: 'text', label: 'Strengths' },
          { key: 'weaknesses', type: 'text', label: 'Weaknesses' },
        ],
      },
      {
        key: 'academic_standardized',
        type: 'object_list',
        label: 'Academic Standardized',
        itemLabel: 'Test',
        itemFields: [
          { key: 'test_name', type: 'string', label: 'Test Name' },
          { key: 'examiner', type: 'string', label: 'Examiner' },
          { key: 'date', type: 'date', label: 'Date' },
          // Reading
          { key: 'reading_composite', type: 'string', label: 'Reading Composite' },
          { key: 'total_reading', type: 'string', label: 'Total Reading' },
          { key: 'basic_reading', type: 'string', label: 'Basic Reading' },
          { key: 'reading_comprehension', type: 'string', label: 'Reading Comprehension' },
          { key: 'word_reading', type: 'string', label: 'Word Reading' },
          { key: 'letter_word_recognition', type: 'string', label: 'Letter & Word Recognition' },
          // Math
          { key: 'math_composite', type: 'string', label: 'Math Composite' },
          { key: 'mathematics', type: 'string', label: 'Mathematics' },
          { key: 'math_computation', type: 'string', label: 'Math Computation' },
          { key: 'math_concepts_applications', type: 'string', label: 'Math Concepts & Applications' },
          { key: 'math_fluency', type: 'string', label: 'Math Fluency' },
          { key: 'numerical_operations', type: 'string', label: 'Numerical Operations' },
          // Written language
          { key: 'written_expression', type: 'string', label: 'Written Expression' },
          { key: 'spelling', type: 'string', label: 'Spelling' },
          // Overall
          { key: 'total_achievement', type: 'string', label: 'Total Achievement' },
          { key: 'full_results', type: 'text', label: 'Full Results' },
        ],
      },
      {
        key: 'cognitive_assessment',
        type: 'object',
        label: 'Cognitive Assessment',
        fields: [
          { key: 'test_name', type: 'string', label: 'Test Name' },
          { key: 'examiner', type: 'string', label: 'Examiner' },
          { key: 'date', type: 'date', label: 'Date' },
          { key: 'format', type: 'string', label: 'Format' },
          { key: 'norms', type: 'string', label: 'Norms' },
          { key: 'fsiq', type: 'string', label: 'FSIQ / GIA' },
          { key: 'gai', type: 'string', label: 'GAI' },
          { key: 'verbal_comprehension', type: 'object', label: 'Verbal Comprehension', fields: COGNITIVE_SCORE_SUBFIELDS },
          { key: 'visual_spatial', type: 'object', label: 'Visual Spatial', fields: COGNITIVE_SCORE_SUBFIELDS },
          { key: 'fluid_reasoning', type: 'object', label: 'Fluid Reasoning', fields: COGNITIVE_SCORE_SUBFIELDS },
          { key: 'working_memory', type: 'object', label: 'Working Memory', fields: COGNITIVE_SCORE_SUBFIELDS },
          { key: 'processing_speed', type: 'object', label: 'Processing Speed', fields: COGNITIVE_SCORE_SUBFIELDS },
          { key: 'full_scale', type: 'object', label: 'Full Scale', fields: COGNITIVE_SCORE_SUBFIELDS },
          { key: 'table_of_scores', type: 'json', label: 'WJ Table of Scores' },
          { key: 'variations', type: 'json', label: 'WJ Variations' },
          { key: 'comparisons', type: 'json', label: 'WJ Comparisons' },
          { key: 'test_session_observations', type: 'json', label: 'WJ Test Session Observations' },
          { key: 'full_results', type: 'text', label: 'Full Results' },
        ],
      },
      {
        key: 'adaptive_assessments',
        type: 'object_list',
        label: 'Adaptive Assessments (Vineland, etc.)',
        itemLabel: 'Adaptive Survey',
        itemFields: ADAPTIVE_ASSESSMENT_ITEM_FIELDS,
      },
      {
        key: 'adaptive_assessment',
        type: 'object',
        label: 'Adaptive Assessment (legacy)',
        fields: ADAPTIVE_ASSESSMENT_FIELDS,
      },
      {
        key: 'behavioral_assessments',
        type: 'object_list',
        label: 'Behavioral Assessments (BASC, etc.)',
        itemLabel: 'Behavioral Survey',
        itemFields: BEHAVIORAL_ASSESSMENT_ITEM_FIELDS,
      },
      {
        key: 'behavioral_assessment',
        type: 'object',
        label: 'Behavioral Assessment (legacy)',
        fields: BEHAVIORAL_ASSESSMENT_ITEM_FIELDS,
      },
      {
        key: 'dibels_assessments',
        type: 'dibels_table',
        label: 'DIBELS / Acadience Benchmarks',
      },
      {
        // Assessment engine: one row per test administration, read from the test's own
        // report and checked against its score scale.
        key: 'standardized_assessments',
        type: 'object_list',
        label: 'Standardized Assessments (verified)',
        itemLabel: 'Administration',
        itemFields: [
          { key: 'test', type: 'string', label: 'Test' },
          { key: 'form', type: 'string', label: 'Form / Rater' },
          { key: 'date', type: 'date', label: 'Test Date' },
          { key: 'date_source', type: 'string', label: 'Date Read From' },
          { key: 'examiner', type: 'string', label: 'Examiner' },
          { key: 'scores', type: 'text', label: 'Scores' },
          { key: 'checks', type: 'text', label: 'Checks / Review Notes' },
          { key: 'pages', type: 'string', label: 'Source Pages' },
        ],
      },
      { key: 'chart_profiles', type: 'json', label: 'Charts & Graph Profiles' },
    ],
  },

  eligibility: {
    label: 'Eligibility',
    fields: [
      { key: 'disability_category', type: 'string', label: 'Disability Category' },
      { key: 'eligibility_decision', type: 'string', label: 'Eligibility Decision' },
      { key: 'decision_date', type: 'date', label: 'Decision Date' },
      { key: 'parent_signature_status', type: 'string', label: 'Parent Signature Status' },
      {
        key: 'exclusionary_factors',
        type: 'object',
        label: 'Exclusionary Factors',
        fields: [
          { key: 'visual', type: 'boolean', label: 'Visual' },
          { key: 'hearing', type: 'boolean', label: 'Hearing' },
          { key: 'motor', type: 'boolean', label: 'Motor' },
          { key: 'intellectual_disability', type: 'boolean', label: 'Intellectual Disability' },
          { key: 'environmental', type: 'boolean', label: 'Environmental' },
          { key: 'cultural', type: 'boolean', label: 'Cultural' },
          { key: 'language', type: 'boolean', label: 'Language' },
        ],
      },
      {
        key: 'team_members',
        type: 'object_list',
        label: 'Team Members',
        itemLabel: 'Team member',
        itemFields: [
          { key: 'role', type: 'string', label: 'Role' },
          { key: 'name', type: 'string', label: 'Name' },
          { key: 'signed', type: 'boolean', label: 'Signed' },
          { key: 'signature_date', type: 'date', label: 'Signature Date' },
        ],
      },
      {
        key: 'translator_interpreter',
        type: 'object',
        label: 'Translator / Interpreter',
        fields: [
          { key: 'required', type: 'boolean', label: '', display: 'primary' },
          { key: 'name', type: 'string', label: 'Name' },
          { key: 'date', type: 'date', label: 'Date' },
          { key: 'signed', type: 'boolean', label: 'Signed' },
        ],
      },
      {
        key: 'notice_of_meeting',
        type: 'object',
        label: 'Notice of Meeting',
        fields: [
          { key: 'date', type: 'date', label: 'Notice Date' },
          { key: 'meeting_date', type: 'date', label: 'Meeting Date' },
          { key: 'time', type: 'string', label: 'Time' },
          { key: 'location', type: 'string', label: 'Location' },
          {
            key: 'checked_boxes',
            type: 'string_list',
            label: 'Checked purposes (IEP, Eligibility, Evaluation, Re-evaluation, Transition, Other)',
          },
        ],
      },
      {
        key: 'eligibility_records',
        type: 'object_list',
        label: 'Eligibility Records (most recent two)',
        itemLabel: 'Record',
        itemFields: [
          { key: 'type', type: 'string', label: 'Type' },
          { key: 'date', type: 'date', label: 'Date' },
          { key: 'decision', type: 'string', label: 'Decision' },
        ],
      },
    ],
  },

  present_levels: {
    label: 'Present Levels',
    fields: [
      { key: 'present_levels_table', type: 'present_levels_table', label: 'Present Levels' },
      { key: 'plaafp_areas', type: 'object_list', label: 'PLAAFP Areas' },
      { key: 'reading_level', type: 'text', label: 'Reading Level' },
      { key: 'writing_level', type: 'text', label: 'Writing Level' },
      { key: 'math_level', type: 'text', label: 'Math Level' },
      { key: 'academic_performance', type: 'text', label: 'Academic Performance' },
      { key: 'functional_performance', type: 'text', label: 'Functional Performance' },
      { key: 'strengths', type: 'string_list', label: 'Strengths' },
      { key: 'weaknesses', type: 'string_list', label: 'Weaknesses' },
      { key: 'impact_on_curriculum', type: 'text', label: 'Impact on Curriculum' },
      { key: 'baseline_data', type: 'object', label: 'Baseline Data', fields: [
        { key: 'reading', type: 'object', label: 'Reading', fields: [
          { key: 'summary', type: 'text', label: 'Summary' },
          { key: 'scores', type: 'string_list', label: 'Scores' },
          { key: 'percentiles', type: 'string_list', label: 'Percentiles' },
        ]},
        { key: 'writing', type: 'object', label: 'Writing', fields: [
          { key: 'summary', type: 'text', label: 'Summary' },
          { key: 'scores', type: 'string_list', label: 'Scores' },
          { key: 'percentiles', type: 'string_list', label: 'Percentiles' },
        ]},
        { key: 'math', type: 'object', label: 'Math', fields: [
          { key: 'summary', type: 'text', label: 'Summary' },
          { key: 'scores', type: 'string_list', label: 'Scores' },
          { key: 'percentiles', type: 'string_list', label: 'Percentiles' },
        ]},
      ]},
    ],
  },

  goals: {
    label: 'Goals',
    fields: [
      { key: 'goals_tables', type: 'goals_client_tables', label: 'Goals' },
    ],
  },

  services_lre: {
    label: 'Services & LRE',
    fields: [
      {
        key: 'services',
        type: 'object_list',
        label: 'Services',
        itemLabel: 'Service',
        itemFields: [
          { key: 'service_type', type: 'string', label: 'Service Type' },
          { key: 'frequency', type: 'string', label: 'Frequency' },
          { key: 'duration', type: 'string', label: 'Duration' },
          { key: 'provider', type: 'string', label: 'Provider' },
          { key: 'location', type: 'string', label: 'Environment' },
        ],
      },
      { key: 'lre_justification', type: 'text', label: 'LRE Justification' },
      { key: 'placement_type', type: 'string', label: 'Placement Type' },
      { key: 'initial_placement_type', type: 'string', label: 'Initial Placement Type' },
      { key: 'change_of_placement', type: 'string', label: 'Change of Placement' },
      { key: 'transportation_provided', type: 'boolean', label: 'Transportation Provided' },
      // Client model IEP (3-25-6-6f1): "Projected date of initiation…" / "Anticipated duration…" (USBE III.J.2.i)
      { key: 'projected_start_date', type: 'date', label: 'Projected Date of Initiation (if other than IEP date)' },
      { key: 'anticipated_duration', type: 'string', label: 'Anticipated Duration (Other)' },
    ],
  },

  accommodations: {
    label: 'Accommodations',
    fields: [
      // Category names mirror the Utah IEP state model form (row 71):
      // p170 "Program Modifications or Supports … Supplementary Aids and Services"
      // and the p172 "Assessment Addendum". Assistive Technology is an assessment
      // accommodation, not its own category, so it is grouped under assessment.
      { key: 'instructional', type: 'string_list', label: 'Instructional Accommodations (Program Modifications & Supplementary Aids/Services)' },
      {
        key: 'program_modifications',
        type: 'object_list',
        label: 'Instructional Accommodations (Program Modifications & Supplementary Aids/Services)',
        itemLabel: 'Accommodation, modification, support, supplementary aid, or service',
        itemTitleKey: 'item',
        itemFields: [
          { key: 'item', type: 'string', label: 'Accommodation, modification, support, supplementary aid, or service' },
          { key: 'frequency', type: 'string', label: 'Frequency' },
        ],
      },
      { key: 'statewide_assessment_accommodations', type: 'string_list', label: 'State-Wide Assessments (RISE/WIDA) — Participation Codes & Accommodations', hidden: true },
      { key: 'lea_assessment_accommodations', type: 'string_list', label: 'LEA/School-Wide Assessments (Acadience/Local) — Participation Codes & Accommodations', hidden: true },
      {
        key: 'accommodations_table1',
        type: 'object_list',
        label: 'Accommodations Table',
        itemLabel: 'Accommodations/ Resources',
        itemTitleKey: 'description',
        itemFields: [
          { key: 'description', type: 'string', label: 'Accommodations/ Resources' },
          { key: 'instruction', type: 'boolean', label: 'Instruction', display: 'check_na' },
          { key: 'lea', type: 'boolean', label: 'LEA', display: 'check_na' },
          { key: 'statewide', type: 'boolean', label: 'Statewide', display: 'check_na' },
        ],
      },
    ],
  },

  assessment_participation: {
    label: 'Assessment Participation',
    fields: [
      { key: 'lea_school_assessments', type: 'string_list', label: 'LEA/School Assessments' },
      {
        key: 'participation_by_assessment',
        type: 'object_list',
        label: 'Statewide Assessment',
        itemLabel: 'Assessment',
        itemFields: [
          { key: 'assessment', type: 'string', label: 'Assessment' },
          { key: 'grade', type: 'string', label: 'Grade' },
          { key: 'current_code', type: 'select', label: 'Current Code', options: ['SA', 'PA', 'PAA', ''] },
          { key: 'next_grade_code', type: 'select', label: 'Next Grade Code', options: ['SA', 'PA', 'PAA', ''] },
          { key: 'wida_code', type: 'string', label: 'WIDA Code' },
        ],
      },
      {
        key: 'assessment_accommodations',
        type: 'object_list',
        label: 'Assessment Accommodations',
        hidden: true,
        itemLabel: 'Accommodation',
        itemFields: [
          { key: 'assessment', type: 'string', label: 'Assessment' },
          { key: 'accommodation', type: 'string', label: 'Code' },
        ],
      },
      { key: 'essential_elements_checked', type: 'boolean', label: 'Essential Elements Checked', hidden: true },
      { key: 'alternate_assessment_consideration', type: 'boolean', label: 'Alternate Assessment Consideration', hidden: true },
      { key: 'alternate_assessment_justification', type: 'text', label: 'Alternate Assessment Justification', hidden: true },
    ],
  },
  behavior: {
    label: 'Behavior',
    fields: [
      {
        key: 'behavior_documents',
        type: 'object_list',
        label: 'Behavior Documents in File',
        itemLabel: 'Document',
        itemFields: [
          { key: 'type', type: 'string', label: 'Type' },
          { key: 'label', type: 'string', label: 'Document' },
          { key: 'page', type: 'string', label: 'Page' },
        ],
      },
    ],
  },

  placement: {
    label: 'Placement',
    fields: [
      { key: 'initial_placement_date', type: 'date', label: 'Initial Placement Date' },
      { key: 'initial_placement_type', type: 'string', label: 'Initial Placement Type' },
      { key: 'placement_type', type: 'string', label: 'Current Placement' },
      { key: 'change_of_placement', type: 'string', label: 'Change of Placement' },
      { key: 'annual_review_of_placement', type: 'boolean', label: 'Annual Review of Placement' },
      {
        key: 'placement_review',
        type: 'object',
        label: 'Annual Review of Placement Checklist',
        fields: [
          { key: 'initial_placement', type: 'boolean', label: 'Initial Placement' },
          { key: 'maintain_current_placement', type: 'boolean', label: 'Maintain Current Placement' },
          { key: 'change_current_placement', type: 'boolean', label: 'Change Current Placement' },
        ],
      },
      {
        key: 'manifestation_determination_documented',
        type: 'boolean',
        label: 'Manifestation Determination Documented',
      },
    ],
  },

  transition_plan: {
    label: 'Transition Plan',
    fields: [
      { key: 'no_transition_plan_needed', type: 'string', label: 'Transition Plan' },
      {
        key: 'transition_checklist',
        type: 'compliance_checklist',
        label: 'Transition Plan Checklist',
        itemFields: [
          { key: 'label', type: 'string', label: 'Compliance Area' },
          { key: 'status', type: 'string', label: 'Compliance Status' },
        ],
      },
      { key: 'student_age', type: 'number', label: 'Student Age' },
      { key: 'transition_exempt', type: 'boolean', label: 'Transition Exempt (under 14)' },
    ],
  },

  iep_compliance: {
    label: 'IEP Compliance',
    fields: [
      {
        key: 'iep_compliance_checklist',
        type: 'compliance_checklist',
        label: 'IEP Compliance Checklist',
        statuses: IEP_COMPLIANCE_CHECKLIST_STATUSES,
        itemFields: [
          { key: 'label', type: 'string', label: 'Compliance Area' },
          { key: 'status', type: 'string', label: 'Compliance Status' },
        ],
      },
      {
        key: 'special_factors',
        type: 'special_factors_grid',
        label: 'Special Factors',
      },
      {
        // Client model IEP, Prior Written Notice (USBE Rules IV.C)
        key: 'prior_written_notice',
        type: 'object',
        label: 'Prior Written Notice',
        fields: [
          { key: 'proposed_actions', type: 'text', label: 'Action(s) Proposed' },
          { key: 'proposed_reasons', type: 'text', label: 'Reasons Proposed (data used)' },
          { key: 'refused_actions', type: 'text', label: 'Action(s) Refused' },
          { key: 'refused_reasons', type: 'text', label: 'Reasons Refused (data used)' },
          { key: 'options_rejected', type: 'text', label: 'Options Considered and Rejected' },
          { key: 'other_factors', type: 'text', label: 'Other Relevant Factors' },
        ],
      },
      {
        key: 'notice_of_meeting',
        type: 'object',
        label: 'Notice of Meeting',
        fields: [
          { key: 'date', type: 'date', label: 'NOM Sent Date' },
          { key: 'meeting_date', type: 'date', label: 'Scheduled Meeting Date' },
          { key: 'time', type: 'string', label: 'Time' },
          { key: 'location', type: 'string', label: 'Location' },
          { key: 'purpose', type: 'string', label: 'Purpose' },
          { key: 'checked_boxes', type: 'string_list', label: 'Checked Purposes' },
        ],
      },
      { key: 'parent_participated', type: 'boolean', label: 'Parent Participated' },
      {
        key: 'team_members',
        type: 'object_list',
        label: 'Team Members',
        itemLabel: 'Member',
        itemFields: [
          { key: 'name', type: 'string', label: 'Name' },
          { key: 'role', type: 'string', label: 'Role' },
          { key: 'signed', type: 'boolean', label: 'Signed' },
          { key: 'signature_date', type: 'date', label: 'Signature Date' },
        ],
      },
      { key: 'esy_considered', type: 'boolean', label: 'ESY Considered' },
      {
        key: 'esy_decision_status',
        type: 'select',
        label: 'ESY Decision',
        options: ['required', 'not_required', 'pending', 'unknown', ''],
      },
      { key: 'regression_recoupment_documented', type: 'boolean', label: 'Regression/Recoupment Documented' },
      { key: 'regression_recoupment_summary', type: 'text', label: 'Regression/Recoupment Summary' },
      { key: 'iep_meeting_date', type: 'date', label: 'IEP Meeting Date' },
    ],
  },

  eligibility_compliance: {
    label: 'Eligibility Compliance',
    fields: [
      { key: 'eligibility_category', type: 'string', label: 'Eligibility Category' },
      { key: 'eligibility_determination', type: 'string', label: 'Eligibility Determination' },
      { key: 'eligibility_meeting_date', type: 'date', label: 'Eligibility Meeting Date' },
      {
        key: 'notice_of_meeting',
        type: 'object',
        label: 'Eligibility Notice of Meeting',
        fields: [
          { key: 'date', type: 'date', label: 'Notice Date' },
          { key: 'meeting_date', type: 'date', label: 'Meeting Date' },
          { key: 'time', type: 'string', label: 'Time' },
          { key: 'location', type: 'string', label: 'Location' },
          {
            key: 'checked_boxes',
            type: 'string_list',
            label: 'Checked purposes (IEP, Eligibility, Evaluation, Re-evaluation, Transition, Other)',
          },
        ],
      },
      {
        key: 'parent_guardian_participation',
        type: 'string',
        label: 'Parent/Guardian Participation',
      },
      {
        key: 'team_participation',
        type: 'object_list',
        label: 'Team Participation',
        itemLabel: 'Team member',
        itemFields: [
          { key: 'role', type: 'string', label: 'Required Team Member' },
          { key: 'name', type: 'string', label: 'Name' },
          { key: 'participated', type: 'string', label: 'Participated?' },
          { key: 'excused', type: 'string', label: 'Excused?' },
        ],
      },
      {
        key: 'eligibility_form_checkboxes',
        type: 'object_list',
        label: 'Eligibility Form Checkboxes',
        itemLabel: 'Checkbox',
        itemFields: [
          { key: 'item', type: 'string', label: 'Checkbox' },
          { key: 'value', type: 'string', label: 'Yes/No' },
        ],
      },
      {
        key: 'translator_interpreter',
        type: 'object',
        label: 'Translator / Interpreter',
        fields: [
          { key: 'required', type: 'boolean', label: '', display: 'primary' },
          { key: 'name', type: 'string', label: 'Name' },
          { key: 'date', type: 'date', label: 'Date' },
          { key: 'signed', type: 'boolean', label: 'Signed' },
        ],
      },
    ],
  },
};

function isEmptyExtractedValue(val) {
  if (val === null || val === undefined || val === '') return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true;
  return false;
}

/** Parse JSON strings from prod pipelines into objects/arrays. */
function parseJsonValue(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
}

const ACCOMMODATIONS_LEGACY_KEYS = {
  instructional_accommodations: 'instructional',
  classroom_accommodations: 'instructional',
  testing_accommodations: 'assessment',
  assessment_accommodations_list: 'assessment',
};

// Behaviour supports are program modifications, so they belong in instructional rather
// than a fourth category — the section reports exactly three. Unlike the aliases above
// this is extra content, so it is appended rather than used only when instructional is
// empty. Only legacy rows still carry it; the backend folds it in on reconcile.
const ACCOMMODATIONS_FOLD_INTO_INSTRUCTIONAL = ['behavior_supports', 'behavior_support'];

const TEAM_ROLE_STRING_RE =
  /\b(teacher|parent|adult student|lea|representative|administrator|psychologist|counselor|specialist|interpreter|translator|coordinator|director|nurse|therapist|aide|paraprofessional|student)\b/i;

/** Normalize team_members to { role, name } objects for object_list UI. */
function normalizeTeamMembersList(members, { stringAsRole = false } = {}) {
  if (!members) return members;
  if (typeof members === 'object' && !Array.isArray(members)) {
    return Object.entries(members).map(([role, name]) => ({
      role,
      name: typeof name === 'string' ? name : formatDisplayValue(name),
    }));
  }
  if (!Array.isArray(members)) return members;
  return members.map((member) => {
    if (typeof member === 'string') {
      const trimmed = member.trim();
      const treatAsRole = stringAsRole || TEAM_ROLE_STRING_RE.test(trimmed);
      return treatAsRole
        ? { role: trimmed, name: null }
        : { name: trimmed, role: null };
    }
    if (!member || typeof member !== 'object') return member;
    const copy = { ...member };
    if (copy.value != null && !copy.name && !copy.role) {
      const val = String(copy.value).trim();
      delete copy.value;
      if (TEAM_ROLE_STRING_RE.test(val)) copy.role = val;
      else copy.name = val;
    }
    return {
      ...copy,
      name: copy.name || copy.member_name || copy.participant || null,
      role: copy.role || copy.position || copy.title || copy.member_role || null,
    };
  });
}

/** Clone extracted_data safely for normalization (never throw on bad JSON). */
function cloneExtractedData(section) {
  const raw = section?.extracted_data;
  if (!raw || typeof raw !== 'object') return {};
  try {
    return JSON.parse(JSON.stringify(raw));
  } catch {
    return { ...raw };
  }
}

const ELIGIBILITY_PURPOSE_BOXES = [
  ['iep_checked', 'IEP'],
  ['eligibility_checked', 'Eligibility'],
  ['evaluation_checked', 'Evaluation'],
  ['reevaluation_checked', 'Re-evaluation'],
  ['transition_checked', 'Transition'],
  ['other_checked', 'Other'],
];

function eligibilityCheckedBoxes(nom) {
  if (!nom || typeof nom !== 'object') return [];
  const boxes = [];
  for (const [key, label] of ELIGIBILITY_PURPOSE_BOXES) {
    if (nom[key] !== true) continue;
    if (key === 'other_checked') {
      const spec = String(nom.other_specify || '').trim();
      boxes.push(spec ? `Other: ${spec}` : 'Other');
    } else {
      boxes.push(label);
    }
  }
  if (boxes.length) return boxes;
  if (Array.isArray(nom.checked_boxes) && nom.checked_boxes.length) {
    return nom.checked_boxes.map((v) => String(v).trim()).filter(Boolean);
  }
  return [];
}

const ACAD_BATTERY_RE = /ktea|woodcock|wj[\s\-]*i{0,3}v|wiat|ctopp|kaufman/i;
const ACAD_NEST_DROP_RE = /^(diagnostic(\s+\d+)?|diagnostic\s+growth)$|i[\-\s]?ready/i;
const ACAD_FLAT_SUBTEST = new Set([
  'letter-word identification', 'letter word identification', 'applied problems',
  'passage comprehension', 'calculation', 'word attack', 'writing samples',
  'oral reading', 'sentence reading fluency', 'math facts fluency',
  'sentence writing fluency', 'reading recall', 'number matrices',
  'math calculation skills', 'math problem solving', 'basic reading skills',
  'letter & word recognition', 'letter and word recognition',
  'math concepts and applications', 'math computation',
  'phonological awareness composite score (pacs)',
  'phonological memory composite score (pmcs)', 'assessment', 'written expression',
]);

const CHART_TYPE_NOISE = new Set(['line', 'bar', 'profile', 'scatter', 'table', 'histogram', 'unknown']);
const CHART_TITLE_NOISE = new Set(['series', 'trial', 'response', 'trial score', 'item score', 'ldsi']);

export function chartProfileHasSubstance(c) {
  if (!c || typeof c !== 'object') return false;
  const titleKey = String(c.title || c.chart_type || c.name || '').trim().toLowerCase();
  if (CHART_TITLE_NOISE.has(titleKey)) return false;
  const summary = String(c.summary || c.description || '').trim();
  if (summary.length > 10) return true;
  const title = String(c.title || c.name || '').trim();
  const ctype = String(c.chart_type || '').trim().toLowerCase();
  if (title && title.toLowerCase() !== ctype && !CHART_TYPE_NOISE.has(title.toLowerCase())) return true;
  if (String(c.x_axis_label || '').trim() || String(c.y_axis_label || '').trim()) return true;
  const series = c.series;
  if (Array.isArray(series)) {
    for (const s of series) {
      const points = s?.data_points;
      if (Array.isArray(points) && points.some((p) => p && (p.y != null || String(p.x || '').trim()))) {
        return true;
      }
    }
  }
  const data = c.data;
  if (data && typeof data === 'object' && !Array.isArray(data) && Object.values(data).some((v) => v != null && v !== '' && !(Array.isArray(v) && !v.length))) {
    return true;
  }
  if (Array.isArray(data) && data.length) return true;
  return false;
}

export function normalizeChartProfilesList(value) {
  const list = Array.isArray(value) ? value : (value && typeof value === 'object' ? [value] : []);
  return list.filter((c) => chartProfileHasSubstance(c));
}

function acadFamilyKey(name) {
  const n = String(name || '').toLowerCase();
  if (n.includes('ktea') || n.includes('kaufman')) return 'ktea';
  if (n.includes('ctopp')) return 'ctopp';
  if (n.includes('wiat')) return 'wiat';
  if (n.includes('woodcock') || /wj[\s-]*iv/.test(n)) {
    return n.includes('cognit') ? 'wj_cog' : 'wj_ach';
  }
  return null;
}

function projectAcademicStandardizedTable(rows) {
  if (!Array.isArray(rows)) return rows;
  const cleaned = rows.filter((r) => r && typeof r === 'object').map((row) => {
    const copy = { ...row };
    const seen = new Set();
    const subtests = [];
    const pushSub = (entry) => {
      if (!entry?.name) return;
      const key = String(entry.name).trim().toLowerCase();
      if (!key || seen.has(key) || ACAD_NEST_DROP_RE.test(entry.name)) return;
      seen.add(key);
      subtests.push(entry);
    };
    const parseLine = (text) => {
      const raw = String(text || '').trim();
      if (!raw || raw.includes('[object Object]')) return null;
      const idx = raw.indexOf(':');
      const name = idx > 0 ? raw.slice(0, idx).trim() : '';
      const rest = idx > 0 ? raw.slice(idx + 1).trim() : raw;
      const ssM = rest.match(/(?:score\s+)?ss\s*[:\s]*(\d+(?:\.\d+)?)/i);
      const pctM = rest.match(/\((\d+(?:\.\d+)?)%\)/);
      const lvlM = rest.match(/[—\-–]\s*([A-Za-z][A-Za-z \-]+)$/);
      if (!name && !ssM) return null;
      const entry = { name: name || 'Score' };
      if (ssM) entry.ss = ssM[1];
      if (pctM) entry.percentile = pctM[1];
      if (lvlM) entry.description = lvlM[1].trim();
      return entry;
    };
    if (Array.isArray(copy.subtests)) {
      for (const s of copy.subtests) {
        if (typeof s === 'string') {
          pushSub(parseLine(s));
          continue;
        }
        if (!s || typeof s !== 'object') continue;
        const name = String(s.name || s.test_name || '').trim();
        const blob = String(s.results || s.full_results || s.summary || s.description || '');
        const ssM = blob.match(/(?:score\s+)?ss\s*[:\s]*(\d+(?:\.\d+)?)/i);
        if (!name || ACAD_NEST_DROP_RE.test(name)) continue;
        if (!ssM && !s.ss && blob.length > 120) continue;
        const entry = { name };
        if (s.ss != null && s.ss !== '') entry.ss = String(s.ss);
        else if (ssM) entry.ss = ssM[1];
        if (s.percentile != null && s.percentile !== '') entry.percentile = String(s.percentile).replace(/%$/, '');
        else {
          const pctM = blob.match(/\((\d+(?:\.\d+)?)%\)/);
          if (pctM) entry.percentile = pctM[1];
        }
        if (s.description) entry.description = String(s.description);
        else {
          const lvlM = blob.match(/[—\-–]\s*([A-Za-z][A-Za-z \-]+)$/);
          if (lvlM) entry.description = lvlM[1].trim();
        }
        pushSub(entry);
      }
    }
    if (!subtests.length && typeof copy.scores === 'string') {
      String(copy.scores).split(/;\s*/).forEach((tok) => pushSub(parseLine(tok)));
    }
    if (!subtests.length) {
      parseClassroomNarrative(copy.full_results || copy.results || copy.summary).forEach(pushSub);
    }
    const acadMetaKeys = new Set([
      'test_name', 'tool', 'date', 'examiner', 'results', 'full_results',
      'summary', 'responder', 'responder_type', 'over_6_years', 'subtests', 'scores',
      'form', 'relation', 'child_name', 'grade', 'age', 'school',
      'rater_name', 'rater_relationship', 'child_dob', 'child_gender',
    ]);
    const promotedKeys = [];
    for (const [k, v] of Object.entries(copy)) {
      if (acadMetaKeys.has(k)) continue;
      if (v === null || v === '') continue;
      const label = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      if (/^(form|relation|child name|grade|age|school)$/i.test(label)) continue;
      if (v && typeof v === 'object' && !Array.isArray(v) && (v.ss != null || v.percentile != null)) {
        const entry = { name: label };
        if (v.ss != null && v.ss !== '') entry.ss = String(v.ss);
        if (v.percentile != null && v.percentile !== '') {
          entry.percentile = String(v.percentile).replace(/%$/, '');
        }
        if (v.description) entry.description = String(v.description);
        pushSub(entry);
        promotedKeys.push(k);
        continue;
      }
      if (typeof v === 'object') continue;
      const ss = String(v).trim();
      if (!ss || !/^\d+(\.\d+)?%?$/.test(ss)) continue;
      pushSub({ name: label, ss });
      promotedKeys.push(k);
    }
    for (const k of promotedKeys) delete copy[k];
    if (subtests.length) copy.subtests = subtests;
    else delete copy.subtests;
    delete copy.scores;
    for (const [k, v] of Object.entries(copy)) {
      if (k === 'over_6_years' || k === 'subtests') continue;
      if (typeof v === 'number') copy[k] = String(v);
    }
    return copy;
  });
  const byFamily = new Map();
  const others = [];
  for (const row of cleaned) {
    const fam = acadFamilyKey(row.test_name || row.tool);
    if (!fam) {
      others.push(row);
      continue;
    }
    const prev = byFamily.get(fam);
    if (!prev || Object.keys(row).length > Object.keys(prev).length) {
      byFamily.set(fam, row);
    }
  }
  const parents = [...byFamily.values()];
  const nested = new Set();
  for (const p of parents) {
    for (const s of p.subtests || []) {
      const n = String(s?.name || '').trim().toLowerCase();
      if (n) nested.add(n);
    }
  }
  const extra = others.filter((row) => {
    const name = String(row.test_name || row.tool || '').trim();
    const lname = name.toLowerCase();
    if (!name || nested.has(lname) || ACAD_FLAT_SUBTEST.has(lname)) return false;
    if (ACAD_NEST_DROP_RE.test(name)) return false;
    return true;
  });
  return [...parents, ...extra];
}

// "dibles" = common OCR / eligibility typo for DIBELS
const CLASSROOM_DIBELS_RE = /dibels|dibles|acadience/i;
const CLASSROOM_CURRICULUM_TOOL_RE = /acadience|dibels|dibles|esgi|i[\-\s]?ready|mobymax|classroom|curriculum|benchmark|mclass|aimsweb|student\s+detail|progress\s+monitoring|recognize numbers|writing numbers|rote count|represent a number|initial (and|&) final|alphabet (letters|sounds)|\brhyme\b/i;
const SCORE_SS_ARRAY_RE = /Score\s+SS\s*\[/i;
const CLASSROOM_MORE_MEASURES_RE = /;?\s*\+\d+\s+more measures\.?\s*$/i;
const CLASSROOM_SCORE_PAIR_RE = /([A-Za-z][A-Za-z0-9 &/'+\-().]{0,80}?)\s*:\s*(?:(?:SS|Score)\s+)?(\d+(?:\.\d+)?)/gi;

function pairsFromClassroomToken(token) {
  const out = [];
  if (!token) return out;
  const re = new RegExp(CLASSROOM_SCORE_PAIR_RE.source, 'gi');
  let m;
  while ((m = re.exec(token)) !== null) {
    const name = String(m[1] || '').replace(/\s+/g, ' ').trim().replace(/^[- ]+|[- ]+$/g, '');
    if (name.length < 2 || /^\d+$/.test(name)) continue;
    out.push({ name, ss: m[2] });
  }
  return out;
}

export function parseClassroomNarrative(text) {
  const blob = String(text || '').replace(CLASSROOM_MORE_MEASURES_RE, '').trim();
  if (!blob || blob.includes('[object Object]')) return [];
  let entries = [];
  const tokens = blob.split(/;\s*/).map((t) => t.trim()).filter(Boolean);
  if (tokens.length >= 2) {
    tokens.forEach((tok) => { entries = entries.concat(pairsFromClassroomToken(tok)); });
  }
  if (entries.length <= 1 && blob.includes(',')) {
    const comma = [];
    blob.split(',').forEach((tok) => { comma.push(...pairsFromClassroomToken(tok.trim())); });
    if (comma.length > entries.length) entries = comma;
  }
  if (!entries.length) entries = pairsFromClassroomToken(blob);
  const seen = new Set();
  return entries.filter((e) => {
    const key = String(e.name || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const ADAPTIVE_DOMAIN_KEYS = [
  'communication', 'daily_living_skills', 'socialization',
  'adaptive_behavior_composite', 'motor_skills',
];

function humanScaleLabel(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function projectSurveyScoreTable(rows, scaleKeys) {
  if (!Array.isArray(rows)) return rows;
  const metaLabels = new Set([
    'form', 'relation', 'child name', 'grade', 'age', 'school',
    'responder', 'responder type', 'examiner', 'test name', 'tool', 'date',
  ]);
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const copy = { ...row };
    const seen = new Set();
    const subtests = [];
    const pushSub = (entry) => {
      if (!entry?.name) return;
      const key = String(entry.name).trim().toLowerCase();
      if (!key || seen.has(key) || metaLabels.has(key)) return;
      const ss = entry.ss != null && entry.ss !== '' ? String(entry.ss) : '';
      // Reject cover values mistakenly stored as scores (PRS-A, Mother, names).
      if (ss && !/^\d+(\.\d+)?%?$/.test(ss) && !/^T\s*\d{2,3}$/i.test(ss)) {
        if (!entry.percentile && !entry.description) return;
      }
      if (ss && /^\d+(\.\d+)?$/.test(ss) && Number(ss) < BASC_T_MIN && !entry.percentile && !entry.description) {
        return;
      }
      seen.add(key);
      const sw = String(entry.strength_weakness || '').trim();
      subtests.push({
        name: entry.name,
        ss: /^\d+(\.\d+)?%?$/.test(ss) || /^T\s*\d{2,3}$/i.test(ss)
          ? ss.replace(/^T\s*/i, '')
          : '',
        percentile: entry.percentile != null && entry.percentile !== ''
          ? String(entry.percentile)
            .replace(/%(?:ile)?$/i, '')
            .replace(/\b(\d+(?:\.\d+)?)(?:st|nd|rd|th)\b/gi, '$1')
            .trim()
          : '',
        description: entry.description || '',
        ...( /^(strength|weakness)$/i.test(sw) ? { strength_weakness: sw } : {}),
      });
    };
    for (const sk of scaleKeys) {
      const val = copy[sk];
      if (val == null || val === '') continue;
      if (typeof val === 'object' && !Array.isArray(val)) {
        const domainSw = String(val.strength_weakness || '').trim();
        pushSub({
          name: humanScaleLabel(sk),
          ss: val.ss ?? val.SS ?? val.t_score ?? val.score ?? '',
          percentile: val.percentile ?? val.percentile_rank ?? '',
          description: val.description ?? val.desc ?? val.range ?? '',
          strength_weakness: /^(strength|weakness)$/i.test(domainSw) ? domainSw : '',
        });
      } else if (typeof val === 'number' || (typeof val === 'string' && /^\d+(\.\d+)?%?$/.test(String(val).trim()))) {
        pushSub({ name: humanScaleLabel(sk), ss: String(val) });
      }
    }
    if (!subtests.length) {
      parseClassroomNarrative(copy.full_results || copy.summary || copy.results).forEach((s) => pushSub(s));
    }
    if (!subtests.length && typeof copy.scores === 'string') {
      parseClassroomNarrative(copy.scores).forEach((s) => pushSub(s));
    }
    for (const sk of scaleKeys) {
      if (subtests.length) delete copy[sk];
    }
    if (subtests.length) copy.subtests = subtests;
    else delete copy.subtests;
    return copy;
  });
}

function bascRaterKind(row) {
  const blob = [row?.test_name, row?.tool, row?.form, row?.responder_type, row?.responder]
    .join(' ')
    .toLowerCase();
  if (/\bprs\b|parent\s+rating|\bparent\b/.test(blob)) return 'parent';
  if (/\btrs\b|teacher\s+rating|\bteacher\b/.test(blob)) return 'teacher';
  return String(row?.responder_type || '').toLowerCase() || 'other';
}

function narrativeIsInvalidBascTLine(text) {
  const blob = String(text || '').trim();
  if (!blob || blob.length > 400) return false;
  const re = /([A-Za-z][A-Za-z0-9 &/'+\-().]{1,40}?)\s*:\s*T\s*[-]?\s*(\d{1,3})\b/gi;
  const vals = [];
  let m;
  while ((m = re.exec(blob))) vals.push(Number(m[2]));
  return vals.length >= 2 && vals.every((n) => Number.isFinite(n) && n < BASC_T_MIN);
}

function normalizeBascDisplayRow(row) {
  const copy = { ...row };
  const rater = bascRaterKind(copy);
  if (rater === 'parent') {
    copy.responder_type = 'parent';
    const form = String(copy.form || '').toUpperCase();
    copy.form = form.startsWith('PRS') ? (copy.form || 'PRS') : 'PRS';
    copy.test_name = 'BASC-3 PRS';
    copy.tool = 'BASC-3';
  } else if (rater === 'teacher') {
    copy.responder_type = 'teacher';
    const form = String(copy.form || '').toUpperCase();
    copy.form = form.startsWith('TRS') ? (copy.form || 'TRS') : 'TRS';
    copy.test_name = 'BASC-3 TRS';
    copy.tool = 'BASC-3';
  }
  const child = String(copy.child_name || '').trim().toLowerCase();
  if (child && String(copy.examiner || '').trim().toLowerCase() === child) {
    delete copy.examiner;
  }
  for (const key of ['summary', 'full_results', 'results']) {
    if (narrativeIsInvalidBascTLine(copy[key])) delete copy[key];
  }
  return copy;
}

function bascRowRichness(row) {
  let n = row?._table_source ? 50 : 0;
  for (const key of BEHAVIORAL_SCALE_KEYS) {
    const val = row?.[key];
    const ss = val && typeof val === 'object' ? val.ss : val;
    if (ss != null && String(ss) !== '' && Number(ss) >= BASC_T_MIN && Number(ss) <= BASC_T_MAX) n += 5;
  }
  return n;
}

function dedupeBascDisplayRows(rows) {
  if (!Array.isArray(rows)) return rows;
  const buckets = new Map();
  const order = [];
  const other = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    if (!/basc/i.test(String(row.test_name || row.tool || ''))) {
      other.push(row);
      continue;
    }
    const norm = normalizeBascDisplayRow(row);
    const key = `${bascRaterKind(norm)}|${String(norm.date || '').trim() || '_'}`;
    const prev = buckets.get(key);
    if (!prev) {
      buckets.set(key, norm);
      order.push(key);
      continue;
    }
    buckets.set(key, bascRowRichness(norm) >= bascRowRichness(prev) ? { ...prev, ...norm } : prev);
  }
  const dated = order.filter((k) => !k.endsWith('|_'));
  for (const key of order) {
    if (!key.endsWith('|_')) continue;
    const rater = key.split('|')[0];
    const matches = dated.filter((k) => k.startsWith(`${rater}|`));
    if (matches.length === 1) continue;
    other.push(buckets.get(key));
  }
  return dated.map((k) => buckets.get(k)).concat(other);
}

export function projectBehavioralAssessmentsTable(rows) {
  return projectSurveyScoreTable(dedupeBascDisplayRows(rows), BEHAVIORAL_SCALE_KEYS);
}

export function projectAdaptiveAssessmentsTable(rows) {
  if (!Array.isArray(rows)) return rows;
  const projected = projectSurveyScoreTable(rows, ADAPTIVE_DOMAIN_KEYS);
  return projected.map((row, i) => {
    const src = rows[i];
    if (!row || !src || typeof src !== 'object') return row;
    const copy = { ...row };
    for (const key of ADAPTIVE_DOMAIN_KEYS) {
      if (src[key] && typeof src[key] === 'object') copy[key] = src[key];
    }
    return copy;
  });
}

function stripCurriculumToolsFromAcademic(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.filter((row) => {
    if (!row || typeof row !== 'object') return false;
    const tool = String(row.test_name || row.tool || '').trim();
    if (CLASSROOM_CURRICULUM_TOOL_RE.test(tool)) return false;
    const blob = String(row.full_results || row.results || '');
    if (SCORE_SS_ARRAY_RE.test(blob)) return false;
    return true;
  });
}

function hasDibelsScoreTable(reports) {
  return Array.isArray(reports) && reports.some((r) => Array.isArray(r?.scores) && r.scores.length);
}

export function projectClassroomAssessmentsTable(rows, dibelsReports) {
  if (!Array.isArray(rows)) return rows;
  const dropDibels = hasDibelsScoreTable(dibelsReports);
  const seen = new Set();
  const out = [];
  const toolDateKey = (row) => {
    let tool = String(row.tool || row.test_name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    if (tool.includes('acadience')) tool = 'acadience';
    else if (tool.includes('dibels')) tool = 'dibels';
    const date = String(row.date || '').trim();
    return tool ? `${tool}|${date}` : '';
  };
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const tool = String(row.tool || row.test_name || '').trim();
    if (dropDibels && CLASSROOM_DIBELS_RE.test(tool)) continue;
    const copy = { ...row };
    if (!copy.test_name && tool) copy.test_name = tool;
    if (!Array.isArray(copy.subtests) || !copy.subtests.length) {
      const parsed = parseClassroomNarrative(copy.full_results || copy.results || copy.summary);
      if (parsed.length) copy.subtests = parsed;
    }
    const key = toolDateKey(copy) || String(copy.full_results || copy.results || tool).trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(copy);
  }
  return out;
}

/** Normalize section data for editing (goals at top level → extracted_data). */
export function normalizeSectionExtractedData(sectionKey, section) {
  const base = cloneExtractedData(section);

  if (sectionKey === 'accommodations') {
    for (const [legacy, canonical] of Object.entries(ACCOMMODATIONS_LEGACY_KEYS)) {
      if (!isEmptyExtractedValue(base[canonical]) || isEmptyExtractedValue(base[legacy])) continue;
      base[canonical] = Array.isArray(base[legacy]) ? base[legacy] : [base[legacy]];
    }
    for (const legacy of ACCOMMODATIONS_FOLD_INTO_INSTRUCTIONAL) {
      if (isEmptyExtractedValue(base[legacy])) continue;
      const extra = Array.isArray(base[legacy]) ? base[legacy] : [base[legacy]];
      const existing = Array.isArray(base.instructional) ? base.instructional : [];
      const seen = new Set(existing.map((v) => String(v).trim().toLowerCase()));
      base.instructional = existing.concat(
        extra.filter((v) => !seen.has(String(v).trim().toLowerCase())),
      );
      delete base[legacy];
    }
    if (Array.isArray(base.accommodations_table1)) {
      const by = new Map();
      for (const row of base.accommodations_table1) {
        if (!row || typeof row !== 'object') continue;
        const desc = String(row.description || '').replace(/\s+/g, ' ').trim();
        if (!desc) continue;
        const key = desc.toLowerCase();
        const prev = by.get(key);
        if (!prev) {
          by.set(key, {
            ...row,
            description: desc,
            instruction: Boolean(row.instruction),
            lea: Boolean(row.lea),
            statewide: Boolean(row.statewide),
          });
        } else {
          prev.instruction = prev.instruction || Boolean(row.instruction);
          prev.lea = prev.lea || Boolean(row.lea);
          prev.statewide = prev.statewide || Boolean(row.statewide);
        }
      }
      base.accommodations_table1 = [...by.values()];
      if (base.accommodations_table1.length) {
        delete base.statewide_assessment_accommodations;
        delete base.lea_assessment_accommodations;
      }
    }
    delete base.lea_accommodations;
    delete base.statewide_accommodations;
    delete base.student_name;
    delete base.accommodations_table;
  }

  if (sectionKey === 'assessment_participation') {
    if (isEmptyExtractedValue(base.participation_by_assessment) && Array.isArray(base.assessments)) {
      base.participation_by_assessment = base.assessments;
    }
    // The Statewide/LEA accommodation lists are one student-level set that the parser
    // copies onto every participation row, so they render identically under each
    // assessment. They now live once per category in the Accommodations section
    // (statewide_assessment_accommodations / lea_assessment_accommodations), so drop
    // them here (section-level + per-row) to avoid the repeated, duplicated display.
    delete base.statewide_accommodations;
    delete base.lea_accommodations;
    delete base.assessment;
    delete base.student_name;
    delete base.accommodations_table1;
    delete base.accommodations_table;
    const statewideMarkers = ['rise', 'wida', 'access', 'dlm', 'aspire', 'act', 'asvab', 'civics', 'sat', 'naep'];
    const isStatewide = (name) => statewideMarkers.some((m) => String(name || '').toLowerCase().includes(m));
    if (Array.isArray(base.participation_by_assessment)) {
      const allRows = base.participation_by_assessment.map((row) => {
        if (!row || typeof row !== 'object') return row;
        const rest = { ...row };
        delete rest.statewide_accommodations;
        delete rest.lea_accommodations;
        if (rest.next_grade_code && rest.grade && !String(rest.grade).includes(',')) {
          const g = parseInt(String(rest.grade).replace(/\D/g, ''), 10);
          if (Number.isFinite(g)) rest.grade = `${g}, ${g + 1}`;
        }
        return rest;
      });
      const leaRows = allRows.filter((r) => r && String(r.assessment || '').trim() && !isStatewide(r.assessment));
      const leaLabel = (r) => {
        const name = String(r.assessment).trim();
        if (!r.current_code) return name;
        return r.next_grade_code
          ? `${name} (${r.current_code}; next grade ${r.next_grade_code})`
          : `${name} (${r.current_code})`;
      };
      const rowNames = new Set(leaRows.map((r) => String(r.assessment).trim().toLowerCase()));
      const stored = Array.isArray(base.lea_school_assessments) ? base.lea_school_assessments : [];
      // Stored names the rows already cover are replaced by the coded label; manual entries stay.
      const manual = stored.filter((s) => {
        const low = String(s || '').trim().toLowerCase();
        return low && ![...rowNames].some((n) => low === n || low.startsWith(`${n} (`));
      });
      if (leaRows.length || !stored.length) {
        base.lea_school_assessments = [...new Set([...leaRows.map(leaLabel), ...manual])];
      }
      base.participation_by_assessment = allRows.filter((r) => r && isStatewide(r.assessment));
    }
    delete base.assessment;
    if (base.alternate_assessment_justification === false) {
      base.alternate_assessment_justification = 'N/A';
    }
    // UAT: show N/A when alternate assessment is not used (false/empty looks like an error)
    if (
      base.alternate_assessment_consideration === false
      || base.alternate_assessment_justification === false
      || base.alternate_assessment_justification === null
      || base.alternate_assessment_justification === ''
    ) {
      if (base.alternate_assessment_consideration === false) {
        base.alternate_assessment_justification = 'N/A';
      }
    }
  }

  if (sectionKey === 'goals') {
    if (Array.isArray(section?.goals) && section.goals.length && !base.goals?.length) {
      base.goals = JSON.parse(JSON.stringify(section.goals));
    }
    delete base.total_goals;
  }

  if (sectionKey === 'eligibility' && base.notice_of_meeting) {
    const nom = parseJsonValue(base.notice_of_meeting);
    if (nom && typeof nom === 'object' && !Array.isArray(nom)) {
      base.notice_of_meeting = {
        ...nom,
        checked_boxes: eligibilityCheckedBoxes(nom),
      };
    }
  }

  // Procedural fields are split across IEP Compliance and Eligibility Compliance; each
  // coercion below is keyed on the field being present, so running both is safe.
  if (sectionKey === 'iep_compliance' || sectionKey === 'eligibility_compliance') {
    const checklist = parseJsonValue(base.iep_checklist ?? base.iep_compliance);
    if (!isEmptyExtractedValue(checklist)) {
      base.iep_compliance = checklist;
    }
    delete base.iep_checklist;

    const iepChecklistFieldKeys = new Set([
      'language_needs', 'braille_needs', 'communication_needs', 'assistive_technology',
      'essential_elements', 'annual_review_of_placement', 'notice_of_meeting_date',
    ]);
    const mergedChecklist = { ...(base.iep_compliance && typeof base.iep_compliance === 'object' ? base.iep_compliance : {}) };
    let mergedAny = false;
    for (const key of iepChecklistFieldKeys) {
      if (base[key] !== undefined && base[key] !== null && base[key] !== '') {
        mergedChecklist[key] = base[key];
        delete base[key];
        mergedAny = true;
      }
    }
    if (mergedAny || Object.keys(mergedChecklist).length) {
      base.iep_compliance = mergedChecklist;
    }
    if (base.iep_compliance && typeof base.iep_compliance === 'object') {
      const cleaned = { ...base.iep_compliance };
      for (const [k, v] of Object.entries(cleaned)) {
        if (k.includes('date') && (v === true || v === false || /^(true|false|yes|no)$/i.test(String(v).trim()))) {
          delete cleaned[k];
        }
      }
      base.iep_compliance = cleaned;
    }

    // Promote scalar meeting_date strings to object_list shape
    for (const key of ['meeting_date', 'prior_meeting_date']) {
      const val = base[key];
      if (typeof val === 'string' && val.trim()) {
        base[key] = [{ date: val.trim() }];
      } else if (Array.isArray(val) && val.length && typeof val[0] === 'string') {
        base[key] = val.map((d) => ({ date: d }));
      }
    }
    if (typeof base.days_between_meetings === 'number') {
      base.days_between_meetings = [{ days: base.days_between_meetings }];
    } else if (Array.isArray(base.days_between_meetings) && typeof base.days_between_meetings[0] === 'number') {
      base.days_between_meetings = base.days_between_meetings.map((d) => ({ days: d }));
    }

    if (base.team_members) {
      base.team_members = normalizeTeamMembersList(base.team_members);
    }

    for (const boolKey of ['parent_participated', 'rights_notice_provided']) {
      if (typeof base[boolKey] === 'string') {
        const s = base[boolKey].trim().toLowerCase();
        if (s === 'true' || s === 'yes' || s === '1') base[boolKey] = true;
        else if (s === 'false' || s === 'no' || s === '0') base[boolKey] = false;
      }
    }

    if (typeof base.consent_obtained === 'boolean') {
      base.consent_obtained = { obtained: base.consent_obtained };
    } else if (typeof base.consent_obtained === 'string') {
      const parsedConsent = parseJsonValue(base.consent_obtained);
      if (parsedConsent && typeof parsedConsent === 'object') {
        base.consent_obtained = parsedConsent;
      }
    }
    if (base.consent_obtained && typeof base.consent_obtained === 'object') {
      const c = base.consent_obtained;
      if (typeof c.parent_signed === 'string') {
        const s = c.parent_signed.trim().toLowerCase();
        if (s === 'true' || s === 'yes') c.parent_signed = true;
        else if (s === 'false' || s === 'no') c.parent_signed = false;
      }
      if (typeof c.obtained === 'string') {
        const s = c.obtained.trim().toLowerCase();
        if (s === 'true' || s === 'yes') c.obtained = true;
        else if (s === 'false' || s === 'no') c.obtained = false;
      }
    }

    if (typeof base.vision_hearing_screenings === 'string') {
      const parsedScreen = parseJsonValue(base.vision_hearing_screenings);
      if (parsedScreen && typeof parsedScreen === 'object') {
        base.vision_hearing_screenings = parsedScreen;
      }
    }

    let nom = parseJsonValue(base.notice_of_meeting);
    if (Array.isArray(nom) && nom.length) nom = nom[0];
    if (nom && typeof nom === 'object' && !Array.isArray(nom)) {
      base.notice_of_meeting = nom;
      const checklist = base.iep_compliance && typeof base.iep_compliance === 'object'
        ? { ...base.iep_compliance }
        : {};
      if (nom.date && !checklist.notice_of_meeting_date) {
        checklist.notice_of_meeting_date = nom.date;
        base.iep_compliance = checklist;
      }
    } else if (base.iep_compliance?.notice_of_meeting_date && !base.notice_of_meeting) {
      base.notice_of_meeting = { date: base.iep_compliance.notice_of_meeting_date };
    }
  }

  if (sectionKey === 'assessment_participation') {
    const acc = base.assessment_accommodations;
    if (Array.isArray(acc) && acc.length && typeof acc[0] === 'string') {
      base.assessment_accommodations = acc.map((s) => ({ assessment: s, accommodation: null }));
    }
  }

  if (sectionKey === 'identity_timeline') {
    for (const key of ['iep_annual_review_date', 'initial_placement_date', 'annual_review_date', 'meeting_date']) {
      if (Array.isArray(base[key])) {
        base[key] = base[key].map((d) => formatDisplayValue(d)).filter(Boolean).join(', ');
      }
    }
  }

  if (sectionKey === 'evaluation') {
    // Legacy aliases for the cognitive card only — never treat the Assessment
    // Data grid (type / PWN / Data Review rows) as a cognitive object.
    for (const legacyKey of ['cognitive', 'cognitive_evaluation']) {
      const legacy = base[legacyKey];
      if (legacy && typeof legacy === 'object' && !Array.isArray(legacy) && !base.cognitive_assessment) {
        base.cognitive_assessment = legacy;
      }
      delete base[legacyKey];
    }

    let cog = parseJsonValue(base.cognitive_assessment);
    if (Array.isArray(cog) && cog.length === 1 && typeof cog[0] === 'object') cog = cog[0];
    if (cog && typeof cog === 'object' && !Array.isArray(cog)) {
      const normalized = { ...cog };
      if (normalized.friq != null && normalized.gai == null) normalized.gai = normalized.friq;
      for (const key of COGNITIVE_INDEX_KEYS) {
        const sub = normalized[key];
        if (!sub || typeof sub !== 'object' || Array.isArray(sub)) continue;
        const subKey = Object.keys(normalized).find((k) => k.toLowerCase() === key) || key;
        const src = normalized[subKey];
        normalized[key] = {
          ss: src.ss ?? src.SS ?? src.standard_score ?? src.score ?? '',
          percentile: src.percentile ?? src.pctile ?? src.percentile_rank ?? '',
          description: src.description ?? src.desc ?? src.range ?? src.classification ?? '',
          ...(src.printed_ss != null ? { printed_ss: src.printed_ss } : {}),
        };
        if (subKey !== key) delete normalized[subKey];
      }
      base.cognitive_assessment = normalized;
    }

    const normalizeAssessmentBlob = (val) => {
      let raw = parseJsonValue(val);
      if (Array.isArray(raw) && raw.length === 1 && typeof raw[0] === 'object') raw = raw[0];
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const row = {
          test_name: raw.test_name || raw.tool || raw.assessment || raw.name || '',
          tool: raw.tool || raw.test_name || '',
          date: raw.date || '',
          examiner: raw.examiner || '',
          composite_score: raw.composite_score || raw.score || raw.ss || raw.composite || '',
          results: raw.results || raw.summary || raw.description || '',
          full_results: raw.full_results || raw.results || raw.narrative || '',
        };
        for (const [k, v] of Object.entries(raw)) {
          if (v == null) continue;
          if (typeof v === 'object' && !Array.isArray(v)) {
            if (v.ss != null || v.percentile != null || v.t_score != null || v.standard_score != null) {
              row[k] = v;
            }
            continue;
          }
          if (row[k] === undefined || row[k] === '') row[k] = v;
        }
        return row;
      }
      if (typeof raw === 'string' && raw.trim()) {
        return { full_results: raw.trim() };
      }
      return val;
    };

    const normalizeAdaptiveRow = (raw) => {
      const row = normalizeAssessmentBlob(raw);
      if (!row || typeof row !== 'object') return row;
      if (!row.responder && row.examiner) row.responder = row.examiner;
      if (!row.summary && row.results) row.summary = row.results;
      const domainKeys = [
        'communication', 'daily_living_skills', 'socialization',
        'adaptive_behavior_composite', 'motor_skills',
      ];
      for (const dk of domainKeys) {
        const sub = row[dk];
        if (!sub || typeof sub !== 'object') continue;
        const swRaw = String(sub.strength_weakness || '').trim();
        const descRaw = String(sub.description ?? sub.desc ?? sub.range ?? '').trim();
        const sw = /^(strength|weakness)$/i.test(swRaw)
          ? swRaw.replace(/^\w/, (c) => c.toUpperCase())
          : (/^(strength|weakness)$/i.test(descRaw)
            ? descRaw.replace(/^\w/, (c) => c.toUpperCase())
            : '');
        row[dk] = {
          ss: sub.ss ?? sub.SS ?? sub.standard_score ?? sub.score ?? '',
          percentile: String(sub.percentile ?? sub.percentile_rank ?? '')
            .replace(/%(?:ile)?$/i, '')
            .replace(/\b(\d+(?:\.\d+)?)(?:st|nd|rd|th)\b/gi, '$1')
            .trim(),
          description: /^(strength|weakness)$/i.test(descRaw) ? '' : descRaw,
          ...(sub.adaptive_level ? { adaptive_level: sub.adaptive_level } : {}),
          ...(sw ? { strength_weakness: sw } : {}),
        };
      }
      return row;
    };

    let adaptiveList = parseJsonValue(base.adaptive_assessments);
    if (!Array.isArray(adaptiveList) && base.adaptive_assessment != null) {
      adaptiveList = [base.adaptive_assessment];
    }
    if (Array.isArray(adaptiveList)) {
      base.adaptive_assessments = projectAdaptiveAssessmentsTable(
        adaptiveList
          .map((row) => normalizeAdaptiveRow(row))
          .filter((row) => row && typeof row === 'object'),
      );
      if (base.adaptive_assessments.length) {
        base.adaptive_assessment = base.adaptive_assessments[0];
      }
    } else if (base.adaptive_assessment != null) {
      base.adaptive_assessment = normalizeAdaptiveRow(base.adaptive_assessment);
      base.adaptive_assessments = projectAdaptiveAssessmentsTable([base.adaptive_assessment]);
      if (base.adaptive_assessments.length) {
        base.adaptive_assessment = base.adaptive_assessments[0];
      }
    }

    let behavioralList = parseJsonValue(base.behavioral_assessments);
    if (!Array.isArray(behavioralList) && base.behavioral_assessment != null) {
      behavioralList = [base.behavioral_assessment];
    }
    const normalizeBehavioralRow = (raw) => {
      const row = normalizeAssessmentBlob(raw);
      if (!row || typeof row !== 'object') return row;
      if (!row.responder && row.examiner) row.responder = row.examiner;
      if (!row.tool) row.tool = 'BASC-3';
      for (const dk of BEHAVIORAL_SCALE_KEYS) {
        const sub = row[dk];
        if (!sub || typeof sub !== 'object') continue;
        row[dk] = normalizeBehavioralSubscale(sub);
      }
      const hasStructured = behavioralRowHasStructuredScores(row);
      for (const key of ['full_results', 'summary', 'results']) {
        if (row[key] == null || row[key] === '') continue;
        const cleaned = sanitizeAssessmentNarrative(row[key]);
        if (cleaned) row[key] = cleaned;
        else delete row[key];
      }
      if (!row.summary && row.full_results && !hasStructured) {
        row.summary = String(row.full_results).slice(0, 500);
      } else if (hasStructured && row.summary && looksLikeFormJsonBlob(row.summary)) {
        delete row.summary;
      }
      return row;
    };
    if (Array.isArray(behavioralList)) {
      base.behavioral_assessments = projectBehavioralAssessmentsTable(
        behavioralList
          .map((row) => normalizeBehavioralRow(row))
          .filter((row) => row && typeof row === 'object'),
      );
      if (base.behavioral_assessments.length) {
        base.behavioral_assessment = base.behavioral_assessments[0];
      }
    } else if (base.behavioral_assessment != null) {
      base.behavioral_assessment = normalizeBehavioralRow(base.behavioral_assessment);
      base.behavioral_assessments = projectBehavioralAssessmentsTable([base.behavioral_assessment]);
      if (base.behavioral_assessments.length) {
        base.behavioral_assessment = base.behavioral_assessments[0];
      }
    }

    if (Array.isArray(base.chart_profiles)) {
      base.chart_profiles = normalizeChartProfilesList(base.chart_profiles);
      if (!base.chart_profiles.length) base.chart_profiles = null;
    }

    if (base.dibels_assessments != null) {
      let dibels = parseJsonValue(base.dibels_assessments);
      if (!Array.isArray(dibels) && dibels && typeof dibels === 'object') {
        dibels = [dibels];
      }
      if (Array.isArray(dibels)) {
        base.dibels_assessments = dibels
          .filter((r) => r && typeof r === 'object')
          .map((report) => {
            const copy = { ...report };
            let scores = copy.scores;
            if (typeof scores === 'string') scores = parseJsonValue(scores);
            copy.scores = Array.isArray(scores)
              ? scores.filter((row) => row && typeof row === 'object')
              : [];
            return copy;
          });
        if (!base.dibels_assessments.length) base.dibels_assessments = null;
      }
    }

    for (const listKey of ['classroom_assessments', 'academic_standardized']) {
      if (!Array.isArray(base[listKey])) continue;
      base[listKey] = base[listKey].map((row) => {
        if (!row || typeof row !== 'object') return row;
        const copy = { ...row };
        if (Array.isArray(copy.date)) {
          copy.date = copy.date.map((d) => formatDisplayValue(d)).filter(Boolean).join(', ');
        }
        return copy;
      });
    }
    if (Array.isArray(base.academic_standardized)) {
      base.academic_standardized = stripCurriculumToolsFromAcademic(
        projectAcademicStandardizedTable(base.academic_standardized),
      );
      if (!base.academic_standardized.length) base.academic_standardized = null;
    }
    if (Array.isArray(base.classroom_assessments)) {
      base.classroom_assessments = projectClassroomAssessmentsTable(
        base.classroom_assessments,
        base.dibels_assessments,
      );
    }
  }

  try {
    return sanitizeExtractedDataForSchema(sectionKey, base);
  } catch (err) {
    console.error('[compliance] sanitizeExtractedDataForSchema failed:', sectionKey, err);
    return base;
  }
}

/** Safe wrapper — prevents a single bad field from crashing the compliance page. */
export function safeNormalizeSectionExtractedData(sectionKey, section) {
  try {
    return normalizeSectionExtractedData(sectionKey, section);
  } catch (err) {
    console.error('[compliance] normalizeSectionExtractedData failed:', sectionKey, err);
    return cloneExtractedData(section);
  }
}

/** Short summary lines for overview section cards when findings are empty. */
export function summarizeSectionExtractedData(sectionKey, section) {
  const ed = safeNormalizeSectionExtractedData(sectionKey, section);
  const lines = [];

  const joinBits = (...bits) => bits.filter(Boolean).join(' · ');

  switch (sectionKey) {
    case 'identity_timeline': {
      const head = joinBits(ed.student_name, ed.grade != null ? `Grade ${ed.grade}` : null);
      if (head) lines.push(head);
      const dates = joinBits(
        ed.iep_annual_review_date && `Annual IEP Due ${ed.iep_annual_review_date}`,
        ed.reevaluation_due_date && `Eligibility Due ${ed.reevaluation_due_date}`,
      );
      if (dates) lines.push(dates);
      if (ed.eligibility_classification) lines.push(ed.eligibility_classification);
      if (ed.case_manager) lines.push(`Case Manager: ${ed.case_manager}`);
      break;
    }
    case 'evaluation': {
      if (ed.disability_category) lines.push(ed.disability_category);
      const marked = (ed.assessment_data || []).filter((r) => r && (r.marked_on_pwn || r.marked_on_data_review));
      if (marked.length) {
        lines.push(`${marked.length} assessment type${marked.length !== 1 ? 's' : ''} marked`);
      } else if (ed.domains_assessed?.length) {
        lines.push(`${ed.domains_assessed.length} domain${ed.domains_assessed.length !== 1 ? 's' : ''} assessed`);
      }
      const cog = ed.cognitive_assessment;
      if (cog?.test_name) lines.push(cog.test_name);
      else if (cog?.fsiq) lines.push(`FSIQ ${cog.fsiq}`);
      const tests = (ed.academic_standardized?.length || 0) + (ed.classroom_assessments?.length || 0);
      if (tests) lines.push(`${tests} assessment${tests !== 1 ? 's' : ''} documented`);
      if (ed.dibels_assessments?.length) {
        const scoreCount = ed.dibels_assessments.reduce(
          (n, r) => n + (Array.isArray(r?.scores) ? r.scores.length : 0),
          0,
        );
        lines.push(`DIBELS ${scoreCount} score${scoreCount !== 1 ? 's' : ''} across ${ed.dibels_assessments.length} report${ed.dibels_assessments.length !== 1 ? 's' : ''}`);
      }
      const adaptiveRows = Array.isArray(ed.adaptive_assessments) ? ed.adaptive_assessments : [];
      adaptiveRows.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        const rtype = String(row.responder_type || '').toLowerCase();
        const label = rtype === 'parent'
          ? 'Parent Vineland'
          : rtype === 'teacher'
            ? 'Teacher Vineland'
            : row.test_name || 'Vineland';
        const abc = row.adaptive_behavior_composite?.ss ?? row.adaptive_behavior_composite;
        const level = row.adaptive_level;
        const bits = [label];
        if (abc != null && abc !== '') bits.push(`ABC ${abc}`);
        if (level) bits.push(level);
        lines.push(bits.join(' · '));
      });
      const behavioralRows = Array.isArray(ed.behavioral_assessments) ? ed.behavioral_assessments : [];
      behavioralRows.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        const rtype = String(row.responder_type || '').toLowerCase();
        const label = rtype === 'parent'
          ? 'Parent BASC'
          : rtype === 'teacher'
            ? 'Teacher BASC'
            : row.test_name || 'BASC';
        const bsi = row.behavioral_symptoms_index?.percentile;
        const bits = [label];
        if (bsi != null && bsi !== '') bits.push(`BSI ${bsi}%`);
        lines.push(bits.join(' · '));
      });
      if (ed.chart_profiles?.length) {
        lines.push(`${ed.chart_profiles.length} chart/graph profile${ed.chart_profiles.length !== 1 ? 's' : ''}`);
      }
      break;
    }
    case 'eligibility': {
      if (ed.disability_category) lines.push(formatDisplayValue(ed.disability_category));
      if (ed.eligibility_decision) lines.push(formatDisplayValue(ed.eligibility_decision));
      if (ed.decision_date) lines.push(`Decision ${formatDisplayValue(ed.decision_date)}`);
      const boxes = ed.notice_of_meeting?.checked_boxes;
      if (Array.isArray(boxes) && boxes.length) lines.push(boxes.join(', '));
      break;
    }
    case 'present_levels': {
      const areas = (ed.present_levels_table || ed.plaafp_areas || []).filter((r) => r && r.area);
      if (areas.length) {
        lines.push(`${areas.length} area${areas.length !== 1 ? 's' : ''} from current IEP`);
        const names = areas.map((r) => r.area).filter(Boolean).slice(0, 4);
        if (names.length) lines.push(names.join(', '));
      }
      break;
    }
    case 'goals': {
      const tables = (ed.goals_tables || []).filter((t) => t && (t.goal_area || t.annual_goal));
      const goals = tables.length ? tables : (Array.isArray(ed.goals) ? ed.goals : []);
      if (goals.length) {
        lines.push(`${goals.length} goal${goals.length !== 1 ? 's' : ''} from current IEP`);
        const areas = [...new Set(goals.map((g) => g?.goal_area).filter(Boolean))];
        if (areas.length) {
          const preview = areas.slice(0, 4).join(', ');
          lines.push(areas.length > 4 ? `${preview}…` : preview);
        }
      }
      break;
    }
    case 'behavior': {
      const docs = (ed.behavior_documents || []).filter((d) => d && (d.type || d.label));
      if (docs.length) {
        lines.push(docs.map((d) => d.type || d.label).join(', '));
        lines.push('Flagged for human review');
      } else {
        lines.push('No behavior documents');
      }
      break;
    }
    case 'transition_plan': {
      const rows = ed.transition_checklist || [];
      const issues = rows.filter((r) => r?.status === 'Not Addressed' || r?.status === 'Needs Review');
      if (ed.transition_exempt) {
        lines.push('Under 14 - No Transition Plan Needed');
      } else if (rows.length) {
        const addressed = rows.filter((r) => r?.status === 'Addressed').length;
        lines.push(`${addressed}/${rows.length} checklist rows addressed`);
        if (issues.length) lines.push(`${issues.length} need review`);
      }
      break;
    }
    case 'iep_compliance': {
      const checklist = ed.iep_compliance_checklist || [];
      if (checklist.length) {
        const issues = checklist.filter((r) => r?.status === 'Not Compliant' || r?.status === 'Not Addressed' || r?.status === 'Needs Review');
        lines.push(`${checklist.length} compliance rows`);
        if (issues.length) lines.push(`${issues.length} need attention`);
      }
      const nom = ed.notice_of_meeting;
      if (nom?.date || nom?.meeting_date) {
        lines.push(`NOM ${formatDisplayValue(nom.date || nom.meeting_date)}`);
      }
      if (ed.esy_decision_status) lines.push(`ESY: ${ed.esy_decision_status}`);
      break;
    }
    case 'eligibility_compliance': {
      if (ed.eligibility_category) lines.push(formatDisplayValue(ed.eligibility_category));
      if (ed.eligibility_determination) {
        lines.push(`Determination: ${formatDisplayValue(ed.eligibility_determination)}`);
      }
      if (ed.eligibility_meeting_date) {
        lines.push(`Meeting ${formatDisplayValue(ed.eligibility_meeting_date)}`);
      }
      if (ed.parent_guardian_participation) {
        lines.push(`Parent: ${formatDisplayValue(ed.parent_guardian_participation)}`);
      }
      break;
    }
    default:
      break;
  }

  return lines.filter(Boolean).slice(0, 4);
}

/** One-line summary for assessment section cards (includes legacy extracted fields). */
export function summarizeAssessmentParticipationData(ed = {}) {
  const rows = ed.participation_by_assessment || [];
  const withCodes = rows.filter((r) => r?.current_code || r?.next_grade_code);
  if (withCodes.length) {
    const codes = withCodes.slice(0, 2).map((r) => {
      const name = r.assessment || r.grade || 'Test';
      const code = r.current_code || r.next_grade_code;
      return `${name}: ${code}`;
    });
    let line = codes.join(' · ');
    if (withCodes.length > 2) line += ` +${withCodes.length - 2}`;
    return line;
  }
  if (rows.length) {
    return `${rows.length} assessment${rows.length !== 1 ? 's' : ''} listed`;
  }
  if (ed.assessment_accommodations?.length) {
    const n = ed.assessment_accommodations.length;
    return `${n} test accommodation${n !== 1 ? 's' : ''}`;
  }

  return 'No participation data';
}

/** Apply schema defaults so all expected fields appear in the editor. */
export function applySchemaDefaults(sectionKey, data) {
  const schema = COMPLIANCE_SECTION_SCHEMAS[sectionKey];
  if (!schema) return { ...data };

  const result = sanitizeExtractedDataForSchema(sectionKey, { ...data });
  for (const field of schema.fields) {
    if (result[field.key] === undefined) {
      result[field.key] = defaultForType(field.type);
    }
  }
  return result;
}

function defaultForType(type) {
  switch (type) {
    case 'string_list':
    case 'object_list':
    case 'assessment_data_table':
    case 'present_levels_table':
    case 'goals_client_tables':
    case 'dibels_table':
    case 'compliance_checklist':
      return [];
    case 'object':
    case 'special_factors_grid':
      return {};
    case 'boolean':
      return null;
    case 'number':
      return null;
    case 'json':
      return {};
    default:
      return '';
  }
}

/** Turn pipeline list items (sometimes objects) into readable display strings. */
export function formatDisplayValue(item) {
  if (item === null || item === undefined) return '';
  if (typeof item === 'string') {
    const s = item.trim();
    return s === '[object Object]' ? '' : s;
  }
  if (typeof item === 'number') return String(item);
  if (typeof item === 'boolean') return item ? 'Yes' : 'No';
  if (Array.isArray(item)) {
    return item
      .map((v) => formatDisplayValue(v))
      .filter((s) => s && s !== '[object Object]')
      .join('; ');
  }
  if (typeof item === 'object') {
    if (item.ss != null && item.description) {
      return `SS ${item.ss} — ${item.description}`;
    }
    if (item.ss != null || item.SS != null) {
      return `SS ${item.ss ?? item.SS}`;
    }
    const textKeys = [
      'full_results', 'results', 'summary', 'narrative', 'description',
      'date', 'accommodation', 'name', 'text', 'value', 'title',
      'detail', 'label', 'support', 'modification', 'type', 'role', 'days',
      'test_name', 'tool', 'examiner', 'composite_score',
    ];
    for (const k of textKeys) {
      if (item[k] != null && String(item[k]).trim() && String(item[k]) !== '[object Object]') {
        const label = item.name || item.test_name;
        const text = String(item[k]).trim();
        return label && k !== 'name' && k !== 'test_name' && k !== 'tool'
          ? `${label}: ${text}`
          : text;
      }
    }
    const parts = Object.entries(item)
      .filter(([, v]) => v != null && typeof v !== 'object' && String(v).trim() !== '' && String(v) !== '[object Object]')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
    if (parts.length) return parts.join(' · ');
    return '';
  }
  const fallback = String(item);
  return fallback === '[object Object]' ? '' : fallback;
}

/** Coerce a single date/string field that may arrive as an array or {date: ...} object from OCR pipelines. */
function normalizeScalarField(value) {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) {
    return value.map((item) => formatDisplayValue(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') return formatDisplayValue(value);
  return String(value);
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) {
    if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
    if (value && typeof value === 'object') {
      return Object.values(value)
        .map((v) => formatDisplayValue(v))
        .filter(Boolean);
    }
    return [];
  }
  return value
    .map((item) => formatDisplayValue(item))
    .filter((s) => s && s !== '[object Object]');
}

/** Coerce stored extracted_data to schema types (prod may have strings where arrays are expected). */
export function coerceFieldValue(field, value) {
  if (value === null || value === undefined) return defaultForType(field.type);

  switch (field.type) {
    case 'string_list':
      return normalizeStringList(value);
    case 'object_list': {
      let list;
      if (Array.isArray(value)) list = value;
      else if (typeof value === 'object') list = [value];
      else if (typeof value === 'string' && value.trim()) list = [{ description: value }];
      else return [];
      return list.map((item) => {
        if (typeof item !== 'object' || item === null) {
          if (typeof item === 'string' && field.key === 'team_members') {
            const trimmed = item.trim();
            const asRole = TEAM_ROLE_STRING_RE.test(trimmed);
            return asRole ? { role: trimmed, name: null } : { name: trimmed, role: null };
          }
          return { value: item };
        }
        const row = { ...item };
        for (const sub of field.itemFields || []) {
          if (row[sub.key] !== undefined) row[sub.key] = coerceFieldValue(sub, row[sub.key]);
        }
        return row;
      });
    }
    case 'object':
    case 'json': {
      let raw = parseJsonValue(value);
      // json fields hold row lists (WJ table_of_scores, chart_profiles); keep them whole.
      if (field.type === 'json' && Array.isArray(raw)) return raw;
      if (Array.isArray(raw) && raw.length === 1 && typeof raw[0] === 'object') raw = raw[0];
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return defaultForType('object');
      const obj = { ...raw };
      for (const sub of field.fields || []) {
        const subKey = Object.keys(obj).find((k) => k.toLowerCase() === sub.key.toLowerCase()) || sub.key;
        if (obj[subKey] !== undefined) {
          obj[sub.key] = coerceFieldValue(sub, obj[subKey]);
          if (subKey !== sub.key) delete obj[subKey];
        }
      }
      return obj;
    }
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') {
        const s = value.trim().toLowerCase();
        if (s === 'true' || s === 'yes' || s === '1') return true;
        if (s === 'false' || s === 'no' || s === '0') return false;
      }
      return null;
    case 'number':
      if (typeof value === 'number') return value;
      if (Array.isArray(value)) {
        const nums = value.map((v) => (typeof v === 'object' && v?.days != null ? v.days : v));
        return nums.length === 1 ? (Number.isNaN(Number(nums[0])) ? null : Number(nums[0])) : null;
      }
      if (value === '') return null;
      return Number.isNaN(Number(value)) ? null : Number(value);
    case 'date': {
      if (value === true || value === false) return '';
      if (typeof value === 'string' && /^(true|false|yes|no)$/i.test(value.trim())) return '';
      const normalized = normalizeScalarField(value);
      if (!normalized) return '';
      if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(normalized)) return normalized;
      if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) return normalized.slice(0, 10);
      return /[A-Za-z]{3,}\s+\d/.test(normalized) ? normalized : '';
    }
    case 'string':
    case 'text':
      return normalizeScalarField(value);
    case 'assessment_data_table':
    case 'present_levels_table':
    case 'goals_client_tables': {
      const list = parseJsonValue(value);
      return Array.isArray(list) ? list.filter((r) => r && typeof r === 'object') : [];
    }
    case 'dibels_table': {
      let dibels = parseJsonValue(value);
      if (!Array.isArray(dibels) && dibels && typeof dibels === 'object') {
        dibels = [dibels];
      }
      if (!Array.isArray(dibels)) return [];
      return dibels
        .filter((r) => r && typeof r === 'object')
        .map((report) => {
          const copy = { ...report };
          let scores = copy.scores;
          if (typeof scores === 'string') scores = parseJsonValue(scores);
          copy.scores = Array.isArray(scores)
            ? scores.filter((row) => row && typeof row === 'object')
            : [];
          return copy;
        });
    }
    case 'compliance_checklist': {
      const list = Array.isArray(value) ? value : [];
      const statuses = Array.isArray(field?.statuses) && field.statuses.length
        ? field.statuses
        : COMPLIANCE_CHECKLIST_STATUSES;
      const valid = new Set(statuses);
      return list
        .filter((row) => row && typeof row === 'object')
        .map((row) => {
          const copy = { ...row };
          const status = copy.status != null ? String(copy.status).trim() : '';
          if (status && !valid.has(status)) {
            const normalized = statuses.find(
              (s) => s.toLowerCase() === status.toLowerCase(),
            );
            copy.status = normalized || 'Needs Review';
          }
          return copy;
        });
    }
    case 'special_factors_grid': {
      const raw = parseJsonValue(value);
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
      const result = {};
      for (const [key, allowed] of Object.entries(SPECIAL_FACTOR_ENUMS)) {
        const val = raw[key];
        result[key] = allowed.includes(val) ? val : (val == null || val === '' ? null : val);
      }
      return result;
    }
    default:
      return value;
  }
}

export function sanitizeExtractedDataForSchema(sectionKey, data) {
  const schema = COMPLIANCE_SECTION_SCHEMAS[sectionKey];
  if (!schema || !data || typeof data !== 'object') return data || {};
  const result = { ...data };
  for (const field of schema.fields) {
    if (result[field.key] !== undefined) {
      result[field.key] = coerceFieldValue(field, result[field.key]);
    }
  }
  return result;
}

export function getSchemaForSection(sectionKey) {
  return COMPLIANCE_SECTION_SCHEMAS[sectionKey] || null;
}
